import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Loader2, ExternalLink, User as UserIcon,
    ChevronDown, ChevronUp, ChevronRight, X,
    MapPin, Briefcase, Building2, FileText, Clock, Inbox, AlertCircle,
    Rocket, Share2, Bookmark, Send, Search, Linkedin,
} from 'lucide-react';
import DidYouApplyModal from './DidYouApplyModal';
import { AnimatePresence, motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getEmployeeTransitions, getRelatedBackground, getTransitionPersonDetail, getRelatedPersonDetail, getProfile, getBestPeople, getCompanyJobs, getCompanyJobDetail } from '../api/insights';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { incrementInsightsProgress } from '../utils/insightsProgress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
const CONNECTION_FILTERS_STORAGE_KEY = 'insightsConnectionFiltersState';
const WORK_TIERS = [
    { key: 'T1', label: 'Same company · Similar role · Same tenure' },
    { key: 'T2', label: 'Same company · Similar role · ±2 yrs' },
    { key: 'T3', label: 'Same company · Same tenure' },
    { key: 'T4', label: 'Same company · ±2 yrs' },
    { key: 'T5', label: 'Same company · Similar role' },
    { key: 'T6', label: 'Same company background' },
];
const EDU_TIERS = [
    { key: 'T1', label: 'Same college · Same branch · Same batch' },
    { key: 'T2', label: 'Same college · Same branch · ±2 yrs' },
    { key: 'T3', label: 'Same college · Same batch · Diff branch' },
    { key: 'T4', label: 'Same college · ±2 yrs · Diff branch' },
    { key: 'T5', label: 'Same college · Same branch' },
    { key: 'T6', label: 'Same college background' },
];
const BEST_PEOPLE_TAG_COLOR = '#3b5ea8';
const BEST_PEOPLE_TAG_BG = 'rgba(59, 94, 168, 0.08)';
const BEST_PEOPLE_TAG_BORDER = 'rgba(59, 94, 168, 0.18)';
const createEmptyBestPeople = () => ({ work: [], college: [], recruiters: [], access_mode: 'full' });
const normalizeBestPeopleData = (data) => ({
    work: Array.isArray(data?.work) ? data.work.slice(0, 5) : [],
    college: Array.isArray(data?.college) ? data.college.slice(0, 5) : [],
    recruiters: Array.isArray(data?.recruiters) ? data.recruiters.slice(0, 5) : [],
    access_mode: data?.access_mode || 'full',
});
/* ── helpers ── */
const avatarColor = (name = '') => {
    const cols = ['#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899', '#EF4444', '#14B8A6'];
    let h = 0;
    for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
    return cols[Math.abs(h) % cols.length];
};
const getInitials = (name = '') => {
    const parts = String(name || '').trim().split(/\s+/);
    if (parts.length === 0 || !parts[0]) return '?';
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};
const cleanJobDescription = (desc) => {
    if (!desc) return '';
    // Handle the "++" issue mentioned by the user - convert to double newlines for better markdown rendering
    return desc.replaceAll('++', '\n\n').trim();
};
const formatJobMarkdown = (desc) => {
    const cleaned = cleanJobDescription(desc);
    if (!cleaned) return '';
    const lines = cleaned.split(/\r?\n/);
    const formatted = [];
    let prevWasList = false;
    let prevWasEmpty = true;
    for (const rawLine of lines) {
        let line = rawLine.trimEnd();
        line = line.replace(/\*{4,}/g, (stars) => {
            const pairs = Math.floor(stars.length / 2);
            const leftover = stars.length % 2 ? '*' : '';
            return Array.from({ length: pairs }).map(() => '**').join(' ') + leftover;
        });
        if (/^\s*\*\*/.test(line)) {
            const rest = line.replace(/^\s*\*\*/, '');
            if (!rest.includes('**')) {
                const text = rest.trim();
                const wordCount = text ? text.split(/\s+/).length : 0;
                const looksLikeHeading = wordCount > 0 && wordCount <= 6 && !/[.:]/.test(text);
                line = looksLikeHeading ? `${line}**` : rest;
            }
        }
        const isEmpty = line.trim().length === 0;
        const isList = /^\s*([-*+]\s+|\d+\.\s+)/.test(line);
        if (isList && !prevWasList && !prevWasEmpty) {
            formatted.push('');
        }
        if (!isList && prevWasList && !isEmpty) {
            formatted.push('');
        }
        formatted.push(line);
        if (/^\s*\*\*[^*]+?\*\*\s*$/.test(line)) {
            formatted.push('');
        }
        prevWasList = isList;
        prevWasEmpty = isEmpty;
    }
    return formatted.join('\n').replace(/\n{3,}/g, '\n\n').trim();
};
const stripMarkdown = (text) => {
    if (!text) return '';
    let out = String(text);
    out = out.replace(/\r\n/g, '\n');
    out = out.replace(/```[\s\S]*?```/g, '');
    out = out.replace(/`([^`]+)`/g, '$1');
    out = out.replace(/!\[.*?\]\(.*?\)/g, '');
    out = out.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
    out = out.replace(/^\s*[-*+]\s+/gm, '• ');
    out = out.replace(/^\s*\d+\.\s+/gm, '• ');
    out = out.replace(/\*\*(.*?)\*\*/g, '$1');
    out = out.replace(/__(.*?)__/g, '$1');
    out = out.replace(/\s+\n/g, '\n');
    out = out.replace(/\n{3,}/g, '\n\n');
    return out.trim();
};
const formatPostedLabel = (postedAt) => {
    if (!postedAt) return null;
    const diff = Date.now() - new Date(postedAt).getTime();
    if (!Number.isFinite(diff) || diff < 0) return null;
    const days = Math.floor(diff / 86400000);
    if (days < 1) return 'Today';
    if (days < 30) return `${days}d ago`;
    return `${Math.floor(days / 30)}mo ago`;
};
const getJobApplyStorageKey = (userId) => `insights_job_applies_${userId || 'anon'}`;
const getDismissedNewTagsStorageKey = (userId) => `insights_dismissed_new_tags_${userId || 'anon'}`;
const readJobApplies = (userId) => {
    if (typeof window === 'undefined') return {};
    try {
        const raw = localStorage.getItem(getJobApplyStorageKey(userId));
        const parsed = raw ? JSON.parse(raw) : {};
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
};
const readDismissedNewTags = (userId) => {
    if (typeof window === 'undefined') return {};
    try {
        const raw = localStorage.getItem(getDismissedNewTagsStorageKey(userId));
        const parsed = raw ? JSON.parse(raw) : {};
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
};
const writeDismissedNewTags = (userId, data) => {
    if (typeof window === 'undefined') return;
    try {
        localStorage.setItem(getDismissedNewTagsStorageKey(userId), JSON.stringify(data || {}));
    } catch {
        // ignore storage failures
    }
};
const FeatureNewBadge = ({ isDark, tooltip }) => {
    const [open, setOpen] = useState(false);
    return (
        <span
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
            onFocus={() => setOpen(true)}
            onBlur={() => setOpen(false)}
            style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
        >
            <span
                style={{
                    fontSize: 10,
                    fontWeight: 900,
                    letterSpacing: '0.05em',
                    padding: '3px 10px',
                    borderRadius: 999,
                    background: isDark ? 'linear-gradient(135deg, rgba(16,185,129,0.26), rgba(5,150,105,0.18))' : 'linear-gradient(135deg, rgba(16,185,129,0.2), rgba(5,150,105,0.1))',
                    border: isDark ? '1px solid rgba(52,211,153,0.55)' : '1px solid rgba(5,150,105,0.34)',
                    color: isDark ? '#6EE7B7' : '#065F46',
                    textTransform: 'uppercase',
                    boxShadow: isDark ? '0 4px 14px rgba(16,185,129,0.16)' : '0 4px 12px rgba(16,185,129,0.12)',
                }}
            >
                New
            </span>
            <span
                aria-hidden
                style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    marginLeft: 4,
                    background: isDark ? '#34D399' : '#059669',
                    boxShadow: isDark ? '0 0 10px rgba(52,211,153,0.7)' : '0 0 8px rgba(5,150,105,0.45)',
                }}
            />
            {open && (
                <span
                    style={{
                        position: 'absolute',
                        top: 'calc(100% + 8px)',
                        right: 0,
                        width: 235,
                        zIndex: 70,
                        padding: '8px 10px',
                        borderRadius: 10,
                        background: isDark ? '#111827' : '#ffffff',
                        border: isDark ? '1px solid rgba(52,211,153,0.35)' : '1px solid rgba(5,150,105,0.25)',
                        boxShadow: isDark ? '0 16px 30px rgba(0,0,0,0.42)' : '0 12px 24px rgba(15,23,42,0.18)',
                        color: isDark ? 'rgba(255,255,255,0.9)' : '#334155',
                        fontSize: 11,
                        fontWeight: 600,
                        lineHeight: 1.45,
                        letterSpacing: '0.01em',
                    }}
                >
                    {tooltip}
                </span>
            )}
        </span>
    );
};
const writeJobApplies = (userId, data) => {
    if (typeof window === 'undefined') return;
    try {
        localStorage.setItem(getJobApplyStorageKey(userId), JSON.stringify(data || {}));
    } catch {
        // ignore storage failures
    }
};
const jobApplyKey = (job) => {
    const id = job?.id;
    if (id != null && String(id).trim()) return `job:${id}`;
    const cleanUrl = cleanLinkedInUrl(job?.job_url || '');
    if (cleanUrl) return `url:${cleanUrl.toLowerCase()}`;
    const fallback = `${String(job?.role || '').trim().toLowerCase()}|${String(job?.location || '').trim().toLowerCase()}`;
    return fallback === '|' ? null : `meta:${fallback}`;
};
const formatAppliedAgo = (diffMs) => {
    const mins = Math.max(0, Math.floor(diffMs / 60000));
    if (mins < 60) {
        const value = Math.max(1, mins);
        return `${value} min${value === 1 ? '' : 's'} ago`;
    }
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} hr${hours === 1 ? '' : 's'} ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days === 1 ? '' : 's'} ago`;
};
const normalizeSkill = (skill) => {
    if (!skill) return '';
    return String(skill)
        .replace(/[\.,\-\/\(\)\+#]/g, ' ')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();
};
const normalizeRoleText = (role) => {
    if (!role) return '';
    return String(role)
        .replace(/[\.,\-\/\(\)\+#]/g, ' ')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();
};
const cleanLinkedInUrl = (url) => {
    if (!url) return '';
    // Remove the part starting from ref
    return url.split(/[?&]ref/)[0];
};
const getBestPeopleTierMeta = (category, tier) => {
    const tiers = category === 'college' ? EDU_TIERS : WORK_TIERS;
    const tierNum = Number(tier);
    if (!Number.isFinite(tierNum)) return null;
    const item = tiers.find((t) => t.key === `T${tierNum}`);
    return item || null;
};
const TierChip = ({ label, tooltip }) => (
    <Tooltip>
        <TooltipTrigger asChild>
            <span
                style={{
                    alignSelf: 'flex-start',
                    display: 'inline-flex',
                    alignItems: 'center',
                    maxWidth: '100%',
                    fontSize: 9,
                    fontWeight: 700,
                    lineHeight: 1.2,
                    letterSpacing: '0.01em',
                    padding: '3px 8px',
                    borderRadius: 999,
                    color: BEST_PEOPLE_TAG_COLOR,
                    background: BEST_PEOPLE_TAG_BG,
                    border: `1px solid ${BEST_PEOPLE_TAG_BORDER}`,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    cursor: 'default',
                }}
            >
                {label}
            </span>
        </TooltipTrigger>
        <TooltipContent side="top" align="start" sideOffset={8} className="max-w-[260px] text-left">
            {tooltip}
        </TooltipContent>
    </Tooltip>
);
/* ── CompanyPeoplePanel ──────────────────────────────────────────────────── */
const CompanyPeoplePanel = ({
    /* required */
    isOpen,
    onClose,
    /* company params */
    sourceOrgId,
    sourceCompanyName,
    destOrgId,
    companyName,
    initialHop = 1,
    startYear,
    endYear,
    role: initialRole = '',
    location: initialLocation = '',
    connectionFilters: externalConnectionFilters,
    crossMatchedEmployeeIds: externalCrossMatched = [],
    matchedEmployeeId = null,
    onOpenNewTab,
    initialOuterTab = 'employees',
    initialSelectedJobId = null,
    initialSelectedJob = null,
    isFromJobsPage = false,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, openLogin, loading: authLoading } = useAuth();
    const { isDark } = useTheme();
    // Theme tokens
    const txtPrimary = isDark ? '#ffffff' : '#0f172a';
    const txtSecondary = isDark ? 'rgba(255,255,255,0.75)' : '#334155';
    const txtMuted = isDark ? 'rgba(255,255,255,0.45)' : '#64748b';
    const txtDim = isDark ? 'rgba(255,255,255,0.25)' : '#94a3b8';
    const borderComp = isDark ? 'rgba(255,255,255,0.06)' : '#e2e8f0';
    const bgComp = isDark ? 'rgba(0,0,0,0.1)' : '#f8fafc';
    const accentColor = isDark ? '#F59E0B' : '#2563eb';
    const accentBg = isDark ? 'rgba(245,158,11,0.12)' : 'rgba(37,99,235,0.08)';
    const accentBorder = isDark ? 'rgba(245,158,11,0.35)' : 'rgba(37,99,235,0.25)';
    const panelBg = isDark ? '#0d1019' : '#ffffff';
    const panelBdr = borderComp;
    const panelClr = txtPrimary;
    const pricingNavState = useMemo(() => ({
        from: 'best_people',
        returnTo: location.pathname,
        returnState: location.state || null,
    }), [location.pathname, location.state]);
    /* ── state ── */
    const [hop, setHop] = useState(initialHop || 1);
    const [roleFilter, setRoleFilter] = useState(initialRole || '');
    const [showOnlyMatched, setShowOnlyMatched] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [allHops, setAllHops] = useState({ 1: [], 2: [], 3: [] });
    const [expandedPersonId, setExpandedPersonId] = useState(null);
    const [detailLoadingId, setDetailLoadingId] = useState(null);
    const [detailError, setDetailError] = useState(null);
    const detailCacheRef = useRef(new Map());
    const detailReqSeqRef = useRef(0);
    const [relatedBackground, setRelatedBackground] = useState([]);
    const [loadingRelated, setLoadingRelated] = useState(false);
    const [profileStatus, setProfileStatus] = useState('loading');
    const [userSkills, setUserSkills] = useState([]);
    const [userRoles, setUserRoles] = useState([]);
    const [peopleTab, setPeopleTab] = useState('transition');
    const [hopNudge, setHopNudge] = useState('');
    const [showRelatedMatchCue, setShowRelatedMatchCue] = useState(false);
    const [bestPeopleOpen, setBestPeopleOpen] = useState(false);
    const [bestPeopleLoading, setBestPeopleLoading] = useState(false);
    const [bestPeople, setBestPeople] = useState(createEmptyBestPeople());
    const bestPeopleCacheRef = useRef(new Map());
    const [bestExpandedId, setBestExpandedId] = useState(null);
    const [bestPeopleLoadedKey, setBestPeopleLoadedKey] = useState('');
    const [showTransitionInfo, setShowTransitionInfo] = useState(false);
    const [dismissedNewTags, setDismissedNewTags] = useState(() => readDismissedNewTags(user?.id));
    // Outer tab: 'employees' | 'jobs'
    const [outerTab, setOuterTab] = useState(initialOuterTab === 'jobs' ? 'jobs' : 'employees');
    const [jobs, setJobs] = useState([]);
    const [jobsLoading, setJobsLoading] = useState(false);
    const [jobsError, setJobsError] = useState(null);
    const jobsListRef = useRef(null);
    const JOBS_PAGE_SIZE = 10;
    const [jobsHasMore, setJobsHasMore] = useState(false);
    const [jobsLoadingMore, setJobsLoadingMore] = useState(false);
    const [expandedJobId, setExpandedJobId] = useState(null);
    const [selectedJob, setSelectedJob] = useState(null);
    const jobDetailCacheRef = useRef(new Map());
    const [jobDetailTick, setJobDetailTick] = useState(0);
    const [jobDetailLoadingId, setJobDetailLoadingId] = useState(null);
    const [jobApplyMap, setJobApplyMap] = useState(() => readJobApplies(user?.id));
    const [jobApplyPulse, setJobApplyPulse] = useState(0);
    const [pendingApplyJob, setPendingApplyJob] = useState(null);
    const [showApplyModal, setShowApplyModal] = useState(false);
    useEffect(() => {
        if (!isOpen) return;
        setOuterTab(initialOuterTab === 'jobs' ? 'jobs' : 'employees');
        setSelectedJob(null);
        setExpandedJobId(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, destOrgId, initialOuterTab]);
    /* ── scroll lock ── */
    useEffect(() => {
        if (isOpen) {
            const originalStyle = window.getComputedStyle(document.body).overflow;
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = originalStyle;
            };
        }
    }, [isOpen]);
    /* connection filters */
    const connectionFilters = useMemo(() => {
        if (externalConnectionFilters && typeof externalConnectionFilters === 'object') {
            return externalConnectionFilters;
        }
        try {
            const raw = sessionStorage.getItem(CONNECTION_FILTERS_STORAGE_KEY);
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            const applied = parsed?.appliedConnectionFilters;
            return applied && typeof applied === 'object' ? applied : null;
        } catch {
            return null;
        }
    }, [externalConnectionFilters]);
    const hasConnectionFilters = useMemo(() => {
        if (!connectionFilters || typeof connectionFilters !== 'object') return false;
        const keys = ['past_companies', 'past_roles', 'tenure_options', 'colleges', 'departments', 'batch_options'];
        return keys.some((k) => Array.isArray(connectionFilters[k]) && connectionFilters[k].length > 0);
    }, [connectionFilters]);
    const employees = useMemo(() => allHops[hop] || [], [allHops, hop]);
    const normalizedRoleFilter = useMemo(
        () => String(roleFilter || '').trim().toLowerCase(),
        [roleFilter]
    );
    const normalizedLocationFilter = useMemo(
        () => String(initialLocation || '').trim().toLowerCase(),
        [initialLocation]
    );
    const normalizedUserSkills = useMemo(() => {
        const seen = new Set();
        const out = [];
        (userSkills || []).forEach((s) => {
            const norm = normalizeSkill(s);
            if (!norm || seen.has(norm)) return;
            seen.add(norm);
            out.push(norm);
        });
        return out;
    }, [userSkills]);
    const normalizedUserRoles = useMemo(() => {
        const seen = new Set();
        const out = [];
        (userRoles || []).forEach((r) => {
            const norm = normalizeRoleText(r);
            if (!norm || seen.has(norm)) return;
            seen.add(norm);
            out.push(norm);
        });
        return out;
    }, [userRoles]);
    const scoreRoleMatch = useCallback((jobRoleNorm) => {
        if (!jobRoleNorm || normalizedUserRoles.length === 0) return 0;
        const jobTokens = jobRoleNorm.split(' ').filter(Boolean);
        const jobTokenSet = new Set(jobTokens);
        let best = 0;
        for (const role of normalizedUserRoles) {
            if (!role) continue;
            if (jobRoleNorm.includes(role) || role.includes(jobRoleNorm)) {
                best = Math.max(best, Math.min(10, role.length));
                continue;
            }
            const roleTokens = role.split(' ').filter(Boolean);
            let overlap = 0;
            roleTokens.forEach((t) => { if (jobTokenSet.has(t)) overlap += 1; });
            if (overlap > best) best = overlap;
        }
        return best;
    }, [normalizedUserRoles]);
    const { matchedJobs, otherJobs } = useMemo(() => {
        const userSkillsList = normalizedUserSkills;
        const matchJobs = [];
        const restJobs = [];
        (jobs || []).forEach((job) => {
            const jobSkillsRaw = Array.isArray(job.skills) ? job.skills : [];
            const jobSkills = jobSkillsRaw.map(normalizeSkill).filter(Boolean);
            let matchedCount = 0;
            if (jobSkills.length > 0 && userSkillsList.length > 0) {
                jobSkills.forEach((js) => {
                    const hit = userSkillsList.some((us) => {
                        if (us.length <= 4) return us === js;
                        return js.includes(us) || us.includes(js);
                    });
                    if (hit) matchedCount += 1;
                });
            }
            const computedRatio = jobSkills.length > 0 ? matchedCount / jobSkills.length : 0;
            const ratio = typeof job.match_ratio === 'number' ? job.match_ratio : computedRatio;
            const isMatch = typeof job.is_match === 'boolean'
                ? job.is_match
                : (jobSkills.length > 0 && computedRatio >= 0.6);
            const roleScore = scoreRoleMatch(normalizeRoleText(job.role));
            const enriched = { ...job, _matchRatio: ratio, _roleScore: roleScore, _isMatch: isMatch };
            if (isMatch) matchJobs.push(enriched);
            else restJobs.push(enriched);
        });
        const sortByRole = (a, b) => {
            if (b._roleScore !== a._roleScore) return b._roleScore - a._roleScore;
            const aDate = a.posted_at ? new Date(a.posted_at).getTime() : 0;
            const bDate = b.posted_at ? new Date(b.posted_at).getTime() : 0;
            return bDate - aDate;
        };
        matchJobs.sort(sortByRole);
        restJobs.sort(sortByRole);
        return { matchedJobs: matchJobs, otherJobs: restJobs };
    }, [jobs, normalizedUserSkills, scoreRoleMatch]);
    const fetchJobsPage = useCallback(async ({ offset = 0, append = false } = {}) => {
        if (!destOrgId) return;
        if (append) setJobsLoadingMore(true);
        else setJobsLoading(true);
        setJobsError(null);
        try {
            const data = await getCompanyJobs(destOrgId, { limit: JOBS_PAGE_SIZE, offset });
            const nextJobs = Array.isArray(data?.jobs) ? data.jobs : [];
            setJobs((prev) => (append ? [...prev, ...nextJobs] : nextJobs));
            setJobsHasMore(Boolean(data?.has_more));
        } catch (err) {
            setJobsError(err.message || 'Failed to load jobs');
            setJobsHasMore(false);
        } finally {
            if (append) setJobsLoadingMore(false);
            else setJobsLoading(false);
        }
    }, [destOrgId]);
    const handleJobsScroll = useCallback((event) => {
        if (!jobsHasMore || jobsLoadingMore || jobsLoading) return;
        const target = event.currentTarget;
        if (!target) return;
        const threshold = 140;
        if (target.scrollTop + target.clientHeight >= target.scrollHeight - threshold) {
            fetchJobsPage({ offset: jobs.length, append: true });
        }
    }, [fetchJobsPage, jobs.length, jobsHasMore, jobsLoading, jobsLoadingMore]);
    const crossMatchedIdSet = useMemo(() => {
        if (!Array.isArray(externalCrossMatched) || externalCrossMatched.length === 0) return new Set();
        return new Set(externalCrossMatched.map((id) => Number(id)).filter((n) => !Number.isNaN(n)));
    }, [externalCrossMatched]);
    const hasCrossMatchesContext = crossMatchedIdSet.size > 0;
    const hasInitialRoleFilter = Boolean(String(initialRole || '').trim());
    const hasLocationFilter = Boolean(normalizedLocationFilter);
    const hasMatchesToggle = hasConnectionFilters || hasCrossMatchesContext || hasInitialRoleFilter || hasLocationFilter;
    const hasTransitionSource = sourceOrgId != null && !Number.isNaN(Number(sourceOrgId));
    const matchedEmployeeIdNum = useMemo(() => {
        const v = matchedEmployeeId != null ? Number(matchedEmployeeId) : null;
        return v != null && !Number.isNaN(v) ? v : null;
    }, [matchedEmployeeId]);
    const isRoleMatchedForPerson = useCallback((person) => {
        if (!normalizedRoleFilter) return false;
        if (person?.role_match) return true;
        return (person?.experience_history || []).some((exp) =>
            String(exp?.role || '').toLowerCase().includes(normalizedRoleFilter)
        );
    }, [normalizedRoleFilter]);
    const isLocationMatchedForPerson = useCallback((person) => {
        if (!normalizedLocationFilter) return false;
        // If backend already flagged it, prefer that (for performance and consistency)
        if (person?.location_match) return true;
        const history = person?.experience_history || [];
        return history.some((exp) => {
            const seg = exp.transition_segment || '';
            // If it's a transition person, check segments >= current hop
            if (seg.startsWith('hop_')) {
                const hNum = parseInt(seg.split('_')[1]);
                if (hNum === hop) {
                    return String(exp?.location || exp?.address || '').toLowerCase().includes(normalizedLocationFilter);
                }
                return false;
            }
            // For related profile or if segment is missing (backend fallback), 
            // check if any current experience at the destination matches
            const atDest = String(exp?.organization || '').toLowerCase() === String(companyName || '').toLowerCase();
            const isCurrent = !exp.end_date;
            if (atDest && isCurrent) {
                return String(exp?.location || exp?.address || '').toLowerCase().includes(normalizedLocationFilter);
            }
            return false;
        });
    }, [normalizedLocationFilter, hop, companyName]);
    const filteredEmployees = useMemo(() => {
        let list = Array.isArray(employees) ? employees : [];
        const hasRoleFilter = normalizedRoleFilter.length > 0;
        // 1. Filtering: Only filter if 'Show Only Matches' is ON
        if (showOnlyMatched && (hasMatchesToggle || hasRoleFilter)) {
            list = list.filter((emp) => (
                crossMatchedIdSet.has(Number(emp?.employee_id))
                || Boolean(emp?.is_match)
                || (hasRoleFilter && isRoleMatchedForPerson(emp))
                || isLocationMatchedForPerson(emp)
            ));
        }
        // 2. Sorting: Prioritize highlights (Cross > Filter > Location > Role)
        list = [...list].sort((a, b) => {
            const aMatched = matchedEmployeeIdNum != null && Number(a?.employee_id) === matchedEmployeeIdNum;
            const bMatched = matchedEmployeeIdNum != null && Number(b?.employee_id) === matchedEmployeeIdNum;
            if (aMatched !== bMatched) return aMatched ? -1 : 1;
            const aCross = crossMatchedIdSet.has(Number(a?.employee_id));
            const bCross = crossMatchedIdSet.has(Number(b?.employee_id));
            if (aCross !== bCross) return aCross ? -1 : 1;
            const aMatch = Boolean(a?.is_match);
            const bMatch = Boolean(b?.is_match);
            if (aMatch !== bMatch) return aMatch ? -1 : 1;
            const aLoc = isLocationMatchedForPerson(a);
            const bLoc = isLocationMatchedForPerson(b);
            if (aLoc !== bLoc) return aLoc ? -1 : 1;
            const aRole = hasRoleFilter && isRoleMatchedForPerson(a);
            const bRole = hasRoleFilter && isRoleMatchedForPerson(b);
            if (aRole !== bRole) return aRole ? -1 : 1;
            return 0;
        });
        return list;
    }, [employees, normalizedRoleFilter, showOnlyMatched, hasMatchesToggle, isRoleMatchedForPerson, isLocationMatchedForPerson, crossMatchedIdSet, matchedEmployeeIdNum]);
    const filteredRelatedBackground = useMemo(() => {
        let list = Array.isArray(relatedBackground) ? relatedBackground : [];
        const hasRoleFilter = normalizedRoleFilter.length > 0;
        // 1. Filtering: Only filter if 'Show Only Matches' is ON
        if (showOnlyMatched && (hasMatchesToggle || hasRoleFilter)) {
            list = list.filter((p) => (
                crossMatchedIdSet.has(Number(p?.employee_id))
                || Boolean(p?.is_match)
                || (hasRoleFilter && isRoleMatchedForPerson(p))
                || isLocationMatchedForPerson(p)
            ));
        }
        // 2. Sorting: Prioritize highlights (Cross > Filter > Location > Role)
        list = [...list].sort((a, b) => {
            const aMatched = matchedEmployeeIdNum != null && Number(a?.employee_id) === matchedEmployeeIdNum;
            const bMatched = matchedEmployeeIdNum != null && Number(b?.employee_id) === matchedEmployeeIdNum;
            if (aMatched !== bMatched) return aMatched ? -1 : 1;
            const aCross = crossMatchedIdSet.has(Number(a?.employee_id));
            const bCross = crossMatchedIdSet.has(Number(b?.employee_id));
            if (aCross !== bCross) return aCross ? -1 : 1;
            const aMatch = Boolean(a?.is_match);
            const bMatch = Boolean(b?.is_match);
            if (aMatch !== bMatch) return aMatch ? -1 : 1;
            const aLoc = isLocationMatchedForPerson(a);
            const bLoc = isLocationMatchedForPerson(b);
            if (aLoc !== bLoc) return aLoc ? -1 : 1;
            const aRole = hasRoleFilter && isRoleMatchedForPerson(a);
            const bRole = hasRoleFilter && isRoleMatchedForPerson(b);
            if (aRole !== bRole) return aRole ? -1 : 1;
            return 0;
        });
        return list;
    }, [relatedBackground, normalizedRoleFilter, showOnlyMatched, hasMatchesToggle, crossMatchedIdSet, isRoleMatchedForPerson, isLocationMatchedForPerson, matchedEmployeeIdNum]);
    /* ── reset when panel opens with new company ── */
    useEffect(() => {
        if (!isOpen) return;
        setHop(initialHop || 1);
        setRoleFilter(initialRole || '');
        setShowOnlyMatched(false);
        setAllHops({ 1: [], 2: [], 3: [] });
        setExpandedPersonId(null);
        setRelatedBackground([]);
        setPeopleTab(hasTransitionSource ? 'transition' : 'related');
        setHopNudge('');
        setBestPeopleOpen(false);
        setBestPeople(createEmptyBestPeople());
        setBestExpandedId(null);
        setBestPeopleLoadedKey('');
        setOuterTab(initialOuterTab === 'jobs' ? 'jobs' : 'employees');
        setUserSkills([]);
        setUserRoles([]);
        setJobs([]);
        setJobsLoading(false);
        setJobsError(null);
        setExpandedJobId(null);
    }, [isOpen, destOrgId, hasTransitionSource]); // eslint-disable-line
    useEffect(() => {
        setJobApplyMap(readJobApplies(user?.id));
    }, [user?.id, jobApplyPulse]);
    useEffect(() => {
        setDismissedNewTags(readDismissedNewTags(user?.id));
    }, [user?.id]);
    useEffect(() => {
        const id = setInterval(() => setJobApplyPulse(Date.now()), 60 * 1000);
        return () => clearInterval(id);
    }, []);
    const recordJobApply = useCallback((job) => {
        const key = jobApplyKey(job);
        if (!key) return;
        const now = Date.now();
        const next = { ...(readJobApplies(user?.id) || {}) };
        next[key] = now;
        writeJobApplies(user?.id, next);
        setJobApplyPulse(now);
        incrementInsightsProgress(user?.id || user?.email, sourceOrgId, 'apps');
    }, [user?.id, user?.email, sourceOrgId]);

    const handleApplyClick = useCallback((job) => {
        setPendingApplyJob(job);
        const handleFocus = () => {
            setTimeout(() => setShowApplyModal(true), 300);
            window.removeEventListener('focus', handleFocus);
        };
        window.addEventListener('focus', handleFocus);
    }, []);

    const handleApplyConfirm = async (didApply) => {
        if (!pendingApplyJob) return;
        const job = pendingApplyJob;
        setShowApplyModal(false);
        setPendingApplyJob(null);
        if (didApply) {
            recordJobApply(job);
        }
    };

    const getJobAppliedLabel = useCallback((job) => {
        const key = jobApplyKey(job);
        if (!key) return '';
        const ts = jobApplyMap[key];
        if (!ts) return '';
        const diff = Date.now() - Number(ts);
        if (!Number.isFinite(diff) || diff < 0) return '';
        return `Applied ${formatAppliedAgo(diff)}`;
    }, [jobApplyMap]);
    /* ── fetch transitions ── */
    useEffect(() => {
        if (!isOpen) return;
        if (authLoading) return;
        if (!user) { openLogin(); return; }
        if (!destOrgId) {
            setError('Missing destination organization information.');
            setLoading(false);
            return;
        }
        if (!hasTransitionSource) {
            setError(null);
            setAllHops({ 1: [], 2: [], 3: [] });
            setLoading(false);
            return;
        }
        const startDate = startYear ? `${startYear}-01-01` : null;
        const endDate = endYear ? `${endYear}-12-31` : null;
        setLoading(true);
        setError(null);
        setExpandedPersonId(null);
        const fetchHop = (h) => getEmployeeTransitions(sourceOrgId, destOrgId, {
            hop: h, startDate, endDate,
            role: initialRole || undefined,
            location: initialLocation || undefined,
            connectionFilters: hasConnectionFilters ? connectionFilters : undefined,
        }).then(data => {
            const list = Array.isArray(data) ? data : [];
            list.sort((a, b) => {
                if ((a.is_match || false) !== (b.is_match || false)) return a.is_match ? -1 : 1;
                if ((a.role_match || false) !== (b.role_match || false)) return a.role_match ? -1 : 1;
                return String(a.employee_name || '').localeCompare(String(b.employee_name || ''));
            });
            return { hop: h, list };
        });
        Promise.all([fetchHop(1), fetchHop(2), fetchHop(3)])
            .then((results) => {
                const newData = {};
                results.forEach(res => { newData[res.hop] = res.list; });
                setAllHops(newData);
            })
            .catch((err) => {
                setError(err.message || 'Failed to load employee transitions');
                setAllHops({ 1: [], 2: [], 3: [] });
            })
            .finally(() => setLoading(false));
    }, [isOpen, sourceOrgId, destOrgId, startYear, endYear, initialRole, initialLocation, hasConnectionFilters, connectionFilters, user, openLogin, authLoading, hasTransitionSource]);
    /* ── profile status ── */
    useEffect(() => {
        if (!isOpen || authLoading || !user) return;
        setProfileStatus('loading');
        getProfile()
            .then((data) => {
                const profile = (data?.profile && typeof data.profile === 'object') ? data.profile : (data && typeof data === 'object' ? data : {});
                const workItems = Array.isArray(profile.work_experiences) ? profile.work_experiences : [];
                const eduItems = Array.isArray(profile.educations) ? profile.educations : [];
                const skills = Array.isArray(profile.skills) ? profile.skills : [];
                const roles = Array.isArray(profile.open_to_roles) ? profile.open_to_roles : [];
                const hasWork = workItems.length > 0 && workItems.some((item) => String(item?.company || item?.organization || '').trim() || String(item?.role || '').trim());
                const hasEducation = eduItems.length > 0 && eduItems.some((item) => String(item?.school || item?.institution || '').trim() || String(item?.degree || item?.field_of_study || '').trim());
                const normalizedSkills = skills
                    .map((s) => (typeof s === 'string' ? s : s?.skill))
                    .map((s) => String(s || '').trim())
                    .filter(Boolean);
                const normalizedRoles = roles
                    .map((r) => (typeof r === 'string' ? r : r?.role))
                    .map((r) => String(r || '').trim())
                    .filter(Boolean);
                setUserSkills(normalizedSkills);
                setUserRoles(normalizedRoles);
                setProfileStatus(hasWork || hasEducation ? 'ready' : 'missing');
            })
            .catch(() => setProfileStatus('error'));
    }, [isOpen, authLoading, user]);
    /* ── fetch related background ── */
    useEffect(() => {
        if (!isOpen || !destOrgId || !user) return;
        if (profileStatus !== 'ready') { setRelatedBackground([]); setLoadingRelated(false); return; }
        const sortRelated = (list) => {
            list.sort((a, b) => {
                if ((a.is_match || false) !== (b.is_match || false)) return a.is_match ? -1 : 1;
                return String(a.employee_name || '').localeCompare(String(b.employee_name || ''));
            });
            return list;
        };
        const startDate = startYear ? `${startYear}-01-01` : null;
        const endDate = endYear ? `${endYear}-12-31` : null;
        setLoadingRelated(true);
        getRelatedBackground(sourceOrgId, destOrgId, {
            startDate, endDate,
            location: initialLocation || undefined,
            connectionFilters: hasConnectionFilters ? connectionFilters : undefined,
        })
            .then((data) => { const list = Array.isArray(data.related) ? data.related : []; setRelatedBackground(sortRelated(list)); })
            .catch(() => setRelatedBackground([]))
            .finally(() => setLoadingRelated(false));
    }, [isOpen, sourceOrgId, destOrgId, startYear, endYear, initialLocation, user, connectionFilters, hasConnectionFilters, profileStatus]);
    /* ── keep default off on open for Find my contact panel ── */
    useEffect(() => {
        if (!isOpen) return;
        setShowOnlyMatched(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, destOrgId]); // intentionally only on open
    useEffect(() => {
        if (!hasMatchesToggle) { setShowRelatedMatchCue(false); return; }
        if (peopleTab === 'related') { setShowRelatedMatchCue(false); return; }
        // Check if ANY person in the related background would be highlighted
        const hasAnyMatchInRelated = (relatedBackground || []).some((p) => {
            const roleMatched = isRoleMatchedForPerson(p);
            const locMatched = isLocationMatchedForPerson(p);
            const isCrossMatched = crossMatchedIdSet.has(Number(p?.employee_id));
            return p?.is_match || roleMatched || locMatched || isCrossMatched;
        });
        setShowRelatedMatchCue(hasAnyMatchInRelated);
    }, [hasMatchesToggle, peopleTab, relatedBackground, isRoleMatchedForPerson, isLocationMatchedForPerson, crossMatchedIdSet]);
    const totalTransitionPeople = useMemo(() => {
        const ids = new Set();
        [1, 2, 3].forEach((h) => {
            (allHops?.[h] || []).forEach((person) => {
                if (person?.employee_id != null) {
                    ids.add(Number(person.employee_id));
                }
            });
        });
        return ids.size;
    }, [allHops]);
    const totalEmployees = peopleTab === 'related' ? filteredRelatedBackground.length : totalTransitionPeople;
    /* ── helpers ── */
    const getMatchDetails = (person) => {
        const details = person?.filter_match_details;
        if (!details || typeof details !== 'object') return { work_matches: [], education_matches: [] };
        return {
            work_matches: Array.isArray(details.work_matches) ? details.work_matches : [],
            education_matches: Array.isArray(details.education_matches) ? details.education_matches : [],
        };
    };
    const buildWorkKey = (item) => [item?.organization || '', item?.role || '', item?.start_date || '', item?.end_date || ''].join('|');
    const experienceMatchedByFilter = (person, exp) => {
        const details = getMatchDetails(person);
        const workKeys = new Set((details.work_matches || []).map(buildWorkKey));
        const matchedByKey = workKeys.has(buildWorkKey(exp));
        let matchedByLocation = false;
        if (normalizedLocationFilter) {
            const isMatchText = String(exp?.location || exp?.address || '').toLowerCase().includes(normalizedLocationFilter);
            if (isMatchText) {
                const seg = exp.transition_segment || '';
                if (seg.startsWith('hop_')) {
                    const hNum = parseInt(seg.split('_')[1]);
                    if (hNum === hop) matchedByLocation = true;
                } else if (!seg) {
                    const atDest = String(exp?.organization || '').toLowerCase() === String(companyName || '').toLowerCase();
                    const isCurrent = !exp.end_date;
                    if (atDest && isCurrent) matchedByLocation = true;
                }
            }
        }
        return matchedByKey || matchedByLocation;
    };
    const personHasFilterEvidence = (person) => {
        const details = getMatchDetails(person);
        return (details.work_matches || []).length > 0 || (details.education_matches || []).length > 0;
    };
    const getTopCompanyNames = (experienceHistory = []) => {
        const names = [];
        const seen = new Set();
        for (const exp of experienceHistory || []) {
            const org = String(exp?.organization || '').trim();
            const key = org.toLowerCase();
            if (!org || seen.has(key)) continue;
            seen.add(key);
            names.push(org);
            if (names.length >= 2) break;
        }
        return names;
    };
    const buildLinkedInSearchUrl = (name, companyNames = []) => {
        const queryParts = [String(name || '').trim(), ...companyNames.slice(0, 2).map((c) => String(c || '').trim()).filter(Boolean), 'linkedin'].filter(Boolean);
        return `https://www.google.com/search?q=${encodeURIComponent(queryParts.join(' '))}`;
    };
    const getCurrentRole = (person) => {
        const history = person?.experience_history || [];
        if (!history.length) return '';
        const atDest = history.find(e => String(e?.organization || '').toLowerCase() === String(companyName || '').toLowerCase());
        if (atDest?.role) return atDest.role;
        return history[0]?.role || '';
    };
    const handleHopChange = (value) => {
        if (peopleTab === 'related') { setHopNudge('Hop works for Transition People. Switch to that tab to change it.'); return; }
        setHopNudge('');
        setHop(value);
    };
    const handlePeopleTabChange = (tab) => {
        if (tab === 'related') setShowRelatedMatchCue(false);
        setPeopleTab(tab);
        setExpandedPersonId(null);
    };
    const loadPersonDetail = (person, type, hopOverride = null) => {
        if (!person?.employee_id) return;
        const effectiveHop = (type === 'transition')
            ? (hopOverride != null ? hopOverride : hop)
            : null;
        const cacheKey = type === 'transition'
            ? `${type}:${effectiveHop || hop}:${person.employee_id}`
            : `${type}:${person.employee_id}`;
        const cached = detailCacheRef.current.get(cacheKey);
        if (cached) {
            return;
        }
        const reqSeq = ++detailReqSeqRef.current;
        setDetailLoadingId(person.employee_id);
        setDetailError(null);
        const startDate = startYear ? `${startYear}-01-01` : null;
        const endDate = endYear ? `${endYear}-12-31` : null;
        const fetcher = type === 'transition'
            ? getTransitionPersonDetail(person.employee_id, {
                sourceOrgId,
                destOrgId,
                hop: effectiveHop || hop,
                startDate,
                endDate,
                connectionFilters: hasConnectionFilters ? connectionFilters : undefined,
            })
            : getRelatedPersonDetail(person.employee_id, {
                destOrgId,
                connectionFilters: hasConnectionFilters ? connectionFilters : undefined,
            });
        fetcher
            .then((data) => {
                if (reqSeq !== detailReqSeqRef.current) return;
                detailCacheRef.current.set(cacheKey, data);
            })
            .catch((err) => {
                if (reqSeq !== detailReqSeqRef.current) return;
                setDetailError(err.message || 'Failed to load details');
            })
            .finally(() => {
                if (reqSeq !== detailReqSeqRef.current) return;
                setDetailLoadingId(null);
            });
    };
    const handlePersonClick = (person, isTransition) => {
        const personId = person?.employee_id;
        const willExpand = expandedPersonId !== personId;
        setExpandedPersonId(prev => prev === personId ? null : personId);
        if (willExpand) {
            incrementInsightsProgress(user?.id || user?.email, sourceOrgId, 'referrals');
            const type = isTransition ? 'transition' : 'related';
            loadPersonDetail(person, type);
        }
    };
    const currentList = peopleTab === 'transition' ? filteredEmployees : filteredRelatedBackground;
    const peopleIndex = useMemo(() => {
        const map = new Map();
        [1, 2, 3].forEach((h) => {
            const list = Array.isArray(allHops?.[h]) ? allHops[h] : [];
            list.forEach((p) => {
                if (p?.employee_id == null) return;
                const id = Number(p.employee_id);
                if (!map.has(id)) {
                    map.set(id, { person: p, source: 'transition', hop: h });
                }
            });
        });
        (relatedBackground || []).forEach((p) => {
            if (p?.employee_id != null && !map.has(Number(p.employee_id))) {
                map.set(Number(p.employee_id), { person: p, source: 'related' });
            }
        });
        return map;
    }, [allHops, relatedBackground]);
    const loadBestPeople = async () => {
        const key = `${sourceOrgId || ''}:${destOrgId || ''}:${companyName || ''}:${startYear || ''}:${endYear || ''}`;
        if (!destOrgId) {
            setBestPeople(createEmptyBestPeople());
            setBestPeopleLoadedKey('');
            return;
        }
        const cached = bestPeopleCacheRef.current.get(key);
        if (cached) {
            setBestPeople(normalizeBestPeopleData(cached));
            setBestPeopleLoadedKey(key);
            return;
        }
        const ids = [
            ...(Array.isArray(allHops?.[1]) ? allHops[1].map((p) => p?.employee_id).filter(Boolean) : []),
            ...(Array.isArray(allHops?.[2]) ? allHops[2].map((p) => p?.employee_id).filter(Boolean) : []),
            ...(Array.isArray(allHops?.[3]) ? allHops[3].map((p) => p?.employee_id).filter(Boolean) : []),
            ...(Array.isArray(relatedBackground) ? relatedBackground.map((p) => p?.employee_id).filter(Boolean) : []),
        ];
        const uniqueIds = Array.from(new Set(ids.map((v) => Number(v)).filter((v) => Number.isFinite(v))));
        if (loading || loadingRelated) {
            return;
        }
        if (uniqueIds.length === 0) {
            const empty = createEmptyBestPeople();
            setBestPeople(empty);
            bestPeopleCacheRef.current.set(key, empty);
            setBestPeopleLoadedKey(key);
            return;
        }
        setBestPeopleLoading(true);
        try {
            const data = await getBestPeople(uniqueIds, { destOrgId });
            const next = normalizeBestPeopleData(data);
            setBestPeople(next);
            bestPeopleCacheRef.current.set(key, next);
            setBestPeopleLoadedKey(key);
        } catch (_) {
            setBestPeople(createEmptyBestPeople());
        } finally {
            setBestPeopleLoading(false);
        }
    };
    const handleToggleBestPeople = () => {
        if (!dismissedNewTags.best_people_to_contact) {
            const next = { ...dismissedNewTags, best_people_to_contact: true };
            setDismissedNewTags(next);
            writeDismissedNewTags(user?.id, next);
        }
        const nextOpen = !bestPeopleOpen;
        setBestPeopleOpen(nextOpen);
        if (nextOpen) {
            loadBestPeople();
        }
    };
    const handleBestPersonClick = (employeeId) => {
        const entry = peopleIndex.get(Number(employeeId));
        if (!entry) return;
        const willExpand = bestExpandedId !== Number(employeeId);
        setBestExpandedId(willExpand ? Number(employeeId) : null);
        if (willExpand) {
            incrementInsightsProgress(user?.id || user?.email, sourceOrgId, 'referrals');
            loadPersonDetail(
                entry.person,
                entry.source === 'transition' ? 'transition' : 'related',
                entry.source === 'transition' ? entry.hop : null
            );
        }
    };
    const bestPeopleWork = useMemo(() => (Array.isArray(bestPeople?.work) ? bestPeople.work.slice(0, 5) : []), [bestPeople]);
    const bestPeopleCollege = useMemo(() => (Array.isArray(bestPeople?.college) ? bestPeople.college.slice(0, 5) : []), [bestPeople]);
    const bestPeopleRecruiters = useMemo(() => (Array.isArray(bestPeople?.recruiters) ? bestPeople.recruiters.slice(0, 5) : []), [bestPeople]);
    const bestPeopleCount = bestPeopleWork.length + bestPeopleCollege.length + bestPeopleRecruiters.length;
    const bestPeopleKey = `${sourceOrgId || ''}:${destOrgId || ''}:${companyName || ''}:${startYear || ''}:${endYear || ''}`;
    const bestPeopleCountReady = bestPeopleLoadedKey === bestPeopleKey;
    useEffect(() => {
        if (!bestPeopleOpen) return;
        loadBestPeople();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bestPeopleOpen, sourceOrgId, destOrgId, companyName, startYear, endYear, allHops, relatedBackground]);
    /* ── fetch jobs when Jobs tab is active ── */
    useEffect(() => {
        if (!isOpen || outerTab !== 'jobs') return;
        if (!destOrgId) {
            setJobs([]);
            setJobsError(null);
            setJobsLoading(false);
            setJobsLoadingMore(false);
            setJobsHasMore(false);
            return;
        }
        setExpandedJobId(null);
        setJobsHasMore(false);
        setJobsLoadingMore(false);
        fetchJobsPage({ offset: 0, append: false });
    }, [isOpen, outerTab, destOrgId, fetchJobsPage]);
    useEffect(() => {
        if (!isOpen || outerTab !== 'jobs' || !initialSelectedJobId) return;
        const targetId = Number(initialSelectedJobId);
        if (!Number.isFinite(targetId)) return;
        const found = (jobs || []).find((j) => Number(j.id) === targetId)
            || (initialSelectedJob && Number(initialSelectedJob.id ?? initialSelectedJob.job_id) === targetId
                ? { ...initialSelectedJob, id: initialSelectedJob.id ?? initialSelectedJob.job_id }
                : null);
        if (!found) return;
        setSelectedJob(found);
        if (!found.description && !jobDetailCacheRef.current.get(found.id)) {
            setJobDetailLoadingId(found.id);
            getCompanyJobDetail(found.id)
                .then((data) => {
                    if (data?.description) {
                        jobDetailCacheRef.current.set(found.id, data.description);
                        setSelectedJob((prev) => (prev && Number(prev.id) === found.id ? { ...prev, description: data.description } : prev));
                        setJobDetailTick((t) => t + 1);
                    }
                })
                .catch(() => { })
                .finally(() => setJobDetailLoadingId(null));
        }
    }, [isOpen, outerTab, jobs, initialSelectedJobId, initialSelectedJob]);
    /* ── PersonRow ── */
    const PersonRow = ({ person, isTransition }) => {
        const isExpanded = expandedPersonId === person.employee_id;
        const roleMatched = isRoleMatchedForPerson(person);
        const locMatched = isLocationMatchedForPerson(person);
        const isCrossMatched = crossMatchedIdSet.has(Number(person?.employee_id));
        const isRecentHireMatched = matchedEmployeeIdNum != null && Number(person?.employee_id) === matchedEmployeeIdNum;
        const isHighlighted = person.is_match || roleMatched || locMatched || isCrossMatched || isRecentHireMatched;
        const color = avatarColor(person.employee_name || '');
        const ini = getInitials(person.employee_name || '');
        const currentRole = getCurrentRole(person);
        const cacheKey = isTransition
            ? `transition:${hop}:${person.employee_id}`
            : `related:${person.employee_id}`;
        const cachedDetail = detailCacheRef.current.get(cacheKey);
        const personWithDetail = cachedDetail ? { ...person, ...cachedDetail } : person;
        const tags = [];
        const addMatchedTag = () => {
            if (!tags.some((t) => t.label === 'Matched')) {
                tags.unshift({ label: 'Matched', color: accentColor, bg: accentBg, border: accentBorder });
            }
        };
        if (isTransition) {
            if (isRecentHireMatched) addMatchedTag();
            if (isCrossMatched) addMatchedTag();
            if (person.is_match) tags.push({ label: 'Filter Matched', color: isDark ? '#60A5FA' : '#3b82f6', bg: isDark ? 'rgba(96,165,250,0.12)' : 'rgba(59,130,246,0.1)', border: isDark ? 'rgba(96,165,250,0.3)' : 'rgba(59,130,246,0.25)' });
            if (locMatched) tags.push({ label: 'Location Matched', color: isDark ? '#34D399' : '#059669', bg: isDark ? 'rgba(52,211,153,0.12)' : 'rgba(5,150,105,0.1)', border: isDark ? 'rgba(52,211,153,0.3)' : 'rgba(5,150,105,0.25)' });
            if (roleMatched) tags.push({ label: 'Role Matched', color: isDark ? '#60A5FA' : '#3b82f6', bg: isDark ? 'rgba(96,165,250,0.12)' : 'rgba(59,130,246,0.1)', border: isDark ? 'rgba(96,165,250,0.3)' : 'rgba(59,130,246,0.25)' });
        } else {
            const connType = person.connection_type;
            const matchDetails = getMatchDetails(person);
            const matchedCompany = matchDetails.work_matches?.[0]?.organization || '';
            const matchedCollege = matchDetails.education_matches?.[0]?.school || '';
            const relatedColor = isDark ? '#7DD3FC' : '#0284c7';
            const relatedBg = isDark ? 'rgba(14,165,233,0.12)' : 'rgba(2,132,199,0.1)';
            const relatedBorder = isDark ? 'rgba(14,165,233,0.25)' : 'rgba(2,132,199,0.2)';
            if (connType === 'past_company_and_college') {
                tags.push({ label: matchedCompany || 'Past Company', color: relatedColor, bg: relatedBg, border: relatedBorder });
                tags.push({ label: matchedCollege || 'College', color: relatedColor, bg: relatedBg, border: relatedBorder });
            } else if (connType === 'past_company') {
                tags.push({ label: matchedCompany || 'Past Company', color: relatedColor, bg: relatedBg, border: relatedBorder });
            } else if (connType === 'college') {
                tags.push({ label: matchedCollege || 'College', color: relatedColor, bg: relatedBg, border: relatedBorder });
            }
            if (isRecentHireMatched) addMatchedTag();
            if (isCrossMatched) addMatchedTag();
            if (person.is_match) tags.push({ label: 'Filter Matched', color: isDark ? '#60A5FA' : '#3b82f6', bg: isDark ? 'rgba(96,165,250,0.12)' : 'rgba(59,130,246,0.1)', border: isDark ? 'rgba(96,165,250,0.3)' : 'rgba(59,130,246,0.25)' });
            if (locMatched) tags.push({ label: 'Location Matched', color: isDark ? '#34D399' : '#059669', bg: isDark ? 'rgba(52,211,153,0.12)' : 'rgba(5,150,105,0.1)', border: isDark ? 'rgba(52,211,153,0.3)' : 'rgba(5,150,105,0.25)' });
        }
        const linkedInUrl = person.linkedin_url || buildLinkedInSearchUrl(
            person.employee_name || `Employee ${person.employee_id}`,
            getTopCompanyNames(personWithDetail.experience_history)
        );
        return (
            <div style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                {/* Row header — clickable to expand */}
                <div
                    onClick={() => handlePersonClick(person, isTransition)}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '10px 16px',
                        cursor: 'pointer',
                        background: isExpanded
                            ? (isDark ? 'rgba(245,158,11,0.07)' : 'rgba(37,99,235,0.06)')
                            : isHighlighted
                                ? (isDark ? 'rgba(59,130,246,0.1)' : 'rgba(37,99,235,0.04)')
                                : 'transparent',
                        borderLeft: isExpanded
                            ? `3px solid ${accentColor}`
                            : isHighlighted
                                ? `3px solid ${isDark ? 'rgba(59,130,246,0.7)' : '#3b82f6'}`
                                : '3px solid transparent',
                        transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'; }}
                    onMouseLeave={e => {
                        if (!isExpanded) e.currentTarget.style.background = isHighlighted ? (isDark ? 'rgba(59,130,246,0.1)' : 'rgba(37,99,235,0.04)') : 'transparent';
                    }}
                >
                    {/* Avatar */}
                    <div style={{
                        width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                        background: `${color}20`, border: `1.5px solid ${color}50`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: 800, color,
                    }}>
                        {ini}
                    </div>
                    {/* Name + meta */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 13, fontWeight: 700, color: txtPrimary, marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {person.employee_name || `Employee ${person.employee_id}`}
                        </p>
                        {currentRole && (
                            <p style={{ fontSize: 11, color: txtSecondary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: tags.length > 0 ? 4 : 0 }}>
                                {currentRole}
                            </p>
                        )}
                        {tags.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                {tags.map((tag, i) => (
                                    <span key={i} style={{
                                        fontSize: 9, fontWeight: 700, letterSpacing: '0.03em', textTransform: 'capitalize',
                                        padding: '2px 6px', borderRadius: 99,
                                        background: tag.bg, color: tag.color, border: `1px solid ${tag.border}`,
                                    }}>
                                        {tag.label}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                    {/* Expand chevron */}
                    <div style={{ color: txtDim, flexShrink: 0 }}>
                        {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </div>
                </div>
                {/* Inline expandable detail */}
                <AnimatePresence initial={false}>
                    {isExpanded && (
                        <motion.div
                            key="detail"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.22, ease: 'easeInOut' }}
                            style={{ overflow: 'hidden' }}
                        >
                            <PersonDetail
                                person={personWithDetail}
                                isTransition={isTransition}
                                linkedInUrl={linkedInUrl}
                                detailLoading={detailLoadingId === person.employee_id}
                                detailError={detailError}
                            />
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        );
    };
    /* ── Inline person detail (dropdown) ── */
    const PersonDetail = ({ person, isTransition, linkedInUrl, detailLoading = false, detailError: detailErrorMsg = null, maskIdentity = false, hideLinkedIn = false }) => {
        const roleMatched = isTransition ? isRoleMatchedForPerson(person) : false;
        const locMatched = isLocationMatchedForPerson(person);
        return (
            <div style={{
                padding: '12px 16px 14px 18px',
                background: isDark ? 'rgba(245,158,11,0.04)' : 'rgba(37,99,235,0.03)',
                borderTop: `1px solid ${borderComp}`,
            }}>
                {/* Filter match evidence */}
                {hasConnectionFilters && person.is_match && personHasFilterEvidence(person) && (
                    <div style={{ marginBottom: 10, padding: '8px 10px', borderRadius: 10, background: isDark ? 'rgba(96,165,250,0.07)' : 'rgba(59,130,246,0.06)', border: isDark ? '1px solid rgba(96,165,250,0.2)' : '1px solid rgba(59,130,246,0.2)' }}>
                        <p style={{ fontSize: 10, fontWeight: 700, color: isDark ? '#60A5FA' : '#2563eb', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>Filter match evidence</p>
                        {(getMatchDetails(person).work_matches || []).length > 0 && (
                            <div style={{ marginBottom: 6 }}>
                                <p style={{ fontSize: 10, color: txtMuted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Work</p>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                                    {(getMatchDetails(person).work_matches || []).map((m, idx) => (
                                        <span key={`work-${idx}`} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 7, background: isDark ? 'rgba(96,165,250,0.1)' : 'rgba(59,130,246,0.08)', color: isDark ? '#93C5FD' : '#1d4ed8', border: isDark ? '1px solid rgba(96,165,250,0.2)' : '1px solid rgba(59,130,246,0.2)' }}>
                                            {m.organization || 'Unknown'}{m.role ? ` · ${m.role}` : ''}{Array.isArray(m.matched_fields) && m.matched_fields.length > 0 ? ` (${m.matched_fields.join(', ')})` : ''}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                        {(getMatchDetails(person).education_matches || []).length > 0 && (
                            <div>
                                <p style={{ fontSize: 10, color: txtMuted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Education</p>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                                    {(getMatchDetails(person).education_matches || []).map((m, idx) => (
                                        <span key={`edu-${idx}`} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 7, background: isDark ? 'rgba(96,165,250,0.1)' : 'rgba(59,130,246,0.08)', color: isDark ? '#93C5FD' : '#1d4ed8', border: isDark ? '1px solid rgba(96,165,250,0.2)' : '1px solid rgba(59,130,246,0.2)' }}>
                                            {m.school || 'Unknown'}{m.degree ? ` · ${m.degree}` : ''}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
                {/* Experience history = Career timeline */}
                {detailLoading && (!(person.experience_history || []).length) && (
                    <div style={{ padding: '4px 0', display: 'flex', alignItems: 'center', gap: 8, color: txtSecondary }}>
                        <Loader2 className="w-4 h-4 animate-spin" style={{ color: accentColor }} />
                        <span style={{ fontSize: 12 }}>Loading career timeline…</span>
                    </div>
                )}
                {detailErrorMsg && !detailLoading && (
                    <div style={{ padding: '4px 0', color: txtMuted, fontSize: 12 }}>
                        {detailErrorMsg}
                    </div>
                )}
                {(person.experience_history || []).length > 0 && (
                    <div>
                        <p style={{ fontSize: 10, fontWeight: 700, color: txtDim, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>
                            Career Timeline of {maskIdentity ? 'Private profile' : (person.employee_name || 'Employee')}
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                            {(person.experience_history || []).map((exp, idx) => {
                                const seg = exp.transition_segment || 'prior';
                                const isHopUsed = seg === `hop_${hop}`;
                                const filterMatchedRow = isTransition && experienceMatchedByFilter(person, exp);
                                let dotColor = isDark ? 'rgba(255,255,255,0.2)' : '#cbd5e1';
                                if (isTransition) {
                                    if (seg === 'source') dotColor = isDark ? '#F59E0B' : '#2563eb';
                                    else if (isHopUsed) dotColor = isDark ? '#34D399' : '#10b981';
                                    else if (seg.startsWith('hop_')) dotColor = isDark ? '#60A5FA' : '#3b82f6';
                                }
                                if (filterMatchedRow) dotColor = isDark ? '#3B82F6' : '#2563eb';
                                const isNow = !exp.end_date || String(exp.end_date).trim().toLowerCase() === 'present' || String(exp.end_date).trim() === '';
                                return (
                                    <div key={idx} style={{ display: 'flex', gap: 12, position: 'relative' }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 12, flexShrink: 0 }}>
                                            <div style={{
                                                width: 9, height: 9, borderRadius: '50%', background: isNow ? dotColor : (isDark ? 'rgba(255,255,255,0.15)' : '#e2e8f0'),
                                                flexShrink: 0, marginTop: 4,
                                                border: `2px solid ${dotColor}`,
                                                boxShadow: isNow && dotColor !== 'rgba(255,255,255,0.2)' && dotColor !== '#cbd5e1' ? `0 0 6px ${dotColor}80` : 'none'
                                            }} />
                                            {idx < (person.experience_history || []).length - 1 && (
                                                <div style={{ width: 1, flex: 1, background: borderComp, minHeight: 16 }} />
                                            )}
                                        </div>
                                        <div style={{
                                            flex: 1, paddingBottom: idx < (person.experience_history || []).length - 1 ? 10 : 0,
                                            background: filterMatchedRow ? (isDark ? 'rgba(59,130,246,0.07)' : 'rgba(37,99,235,0.04)') : 'transparent',
                                            borderRadius: filterMatchedRow ? 6 : 0,
                                            padding: filterMatchedRow ? '4px 8px 8px' : '0 0 10px',
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                                <p style={{ fontSize: 12, fontWeight: 700, color: isNow ? txtPrimary : txtSecondary, margin: 0 }}>
                                                    {exp.organization || 'Unknown'}
                                                </p>
                                                {isHopUsed && isTransition && (
                                                    <span style={{ fontSize: 8, fontWeight: 700, padding: '1px 6px', borderRadius: 99, background: isDark ? 'rgba(52,211,153,0.15)' : 'rgba(16,185,129,0.1)', color: isDark ? '#34D399' : '#059669', border: isDark ? '1px solid rgba(52,211,153,0.3)' : '1px solid rgba(16,185,129,0.25)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                        {isNow ? 'NOW' : 'TARGET'}
                                                    </span>
                                                )}
                                                {isNow && !isHopUsed && (
                                                    <span style={{ fontSize: 8, fontWeight: 700, padding: '1px 6px', borderRadius: 99, background: accentBg, color: accentColor, border: `1px solid ${accentBorder}`, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                        NOW
                                                    </span>
                                                )}
                                            </div>
                                            {exp.role && (
                                                <p style={{ fontSize: 11, color: txtMuted, marginBottom: 1 }}>{exp.role}</p>
                                            )}
                                            <p style={{ fontSize: 10, color: txtDim }}>
                                                {[exp.start_date, exp.end_date].filter(Boolean).join(' – ') || '—'}
                                                {(exp.location || exp.address) && ` · ${exp.location || exp.address}`}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
                {/* Action buttons */}
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    {linkedInUrl && !hideLinkedIn && (
                        <a
                            href={linkedInUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                            style={{
                                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                                padding: '9px 14px', borderRadius: 9, fontSize: 12, fontWeight: 700,
                                background: 'rgba(96,165,250,0.1)', color: '#60A5FA',
                                border: '1px solid rgba(96,165,250,0.25)', textDecoration: 'none', cursor: 'pointer',
                                transition: 'all 0.15s',
                            }}
                        >
                            <ExternalLink size={11} /> View LinkedIn
                        </a>
                    )}
                    {linkedInUrl && hideLinkedIn && (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate('/pricing', { state: { ...pricingNavState, from: 'best_people_link_lock' } });
                            }}
                            style={{
                                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                                padding: '9px 14px', borderRadius: 9, fontSize: 12, fontWeight: 700,
                                background: isDark ? 'rgba(245,158,11,0.12)' : 'rgba(37,99,235,0.08)',
                                color: accentColor,
                                border: `1px solid ${accentBorder}`,
                                cursor: 'pointer',
                                transition: 'all 0.15s',
                            }}
                        >
                            <Linkedin size={12} /> Unlock LinkedIn
                        </button>
                    )}
                </div>
            </div>
        );
    };
    /* ── Empty state for person list ── */
    const transitionEmptyMessage = useMemo(() => {
        const unfilteredEmployeesInHop = Array.isArray(employees) && employees.length > 0;
        const hasActiveTextFilter = Boolean(String(roleFilter || '').trim());
        const hasActiveMatchToggle = showOnlyMatched && hasMatchesToggle;
        if (!unfilteredEmployeesInHop) {
            return `No transition people found for this hop.`;
        }
        if (hasActiveTextFilter && hasActiveMatchToggle) {
            return `No people in this hop match both your search "${roleFilter}" and active filters.`;
        }
        if (hasActiveTextFilter) {
            return `No people in this hop match your search "${roleFilter}".`;
        }
        if (hasActiveMatchToggle) {
            return `No people match your active filters in this hop. Try turning off "Show only matches" to see everyone.`;
        }
        return 'No one found.';
    }, [employees, roleFilter, showOnlyMatched, hasMatchesToggle]);
    const relatedEmptyMessage = useMemo(() => {
        const unfilteredRelated = Array.isArray(relatedBackground) && relatedBackground.length > 0;
        const hasActiveTextFilter = Boolean(String(roleFilter || '').trim());
        const hasActiveMatchToggle = showOnlyMatched && hasMatchesToggle;
        if (!unfilteredRelated) {
            return (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '20px 0' }}>
                    <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(239,68,68,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <AlertCircle size={20} color="#EF4444" />
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: isDark ? 'rgba(255,255,255,0.9)' : '#0f172a' }}>Network Not Found</p>
                        <p style={{ margin: '4px 0 0', fontSize: 11, color: isDark ? 'rgba(255,255,255,0.4)' : '#64748b' }}>No people related to your background were found for this company.</p>
                    </div>
                </div>
            );
        }
        if (hasActiveTextFilter && hasActiveMatchToggle) {
            return `No one in your network matches both your search "${roleFilter}" and current filters.`;
        }
        if (hasActiveTextFilter) {
            return `No one in your network matches your search "${roleFilter}".`;
        }
        if (hasActiveMatchToggle) {
            return `No one in your network matches your current filters. Try turning off "Show only matches".`;
        }
        return 'No one found.';
    }, [relatedBackground, roleFilter, showOnlyMatched, hasMatchesToggle, isDark]);
    const handleClosePanel = () => {
        bestPeopleCacheRef.current = new Map();
        setBestPeople(createEmptyBestPeople());
        setBestPeopleLoadedKey('');
        setBestPeopleOpen(false);
        setBestExpandedId(null);
        jobDetailCacheRef.current = new Map();
        onClose();
    };
    const getJobDescription = useCallback(async (jobId) => {
        if (!jobId) return null;
        const cached = jobDetailCacheRef.current.get(jobId);
        if (cached != null) return cached;
        try {
            setJobDetailLoadingId(jobId);
            const data = await getCompanyJobDetail(jobId);
            const desc = data?.description || '';
            jobDetailCacheRef.current.set(jobId, desc);
            setJobDetailTick((t) => t + 1);
            return desc;
        } catch {
            jobDetailCacheRef.current.set(jobId, '');
            setJobDetailTick((t) => t + 1);
            return '';
        } finally {
            setJobDetailLoadingId(null);
        }
    }, []);
    useEffect(() => {
        if (!expandedJobId) return;
        const job = [...matchedJobs, ...otherJobs].find((j) => j.id === expandedJobId);
        if (!job) return;
        if (job.description) return;
        if (jobDetailCacheRef.current.has(job.id)) return;
        getJobDescription(job.id);
    }, [expandedJobId, matchedJobs, otherJobs, getJobDescription, jobDetailTick]);
    useEffect(() => {
        if (!selectedJob) return;
        if (selectedJob.description) return;
        if (jobDetailCacheRef.current.has(selectedJob.id)) return;
        getJobDescription(selectedJob.id);
    }, [selectedJob, getJobDescription, jobDetailTick]);
    const markdownComponents = {
        h1: ({ children }) => <h1 style={{ fontSize: '1.5em', fontWeight: 800, color: txtPrimary, margin: '24px 0 12px' }}>{children}</h1>,
        h2: ({ children }) => <h2 style={{ fontSize: '1.3em', fontWeight: 800, color: txtPrimary, margin: '20px 0 10px' }}>{children}</h2>,
        h3: ({ children }) => <h3 style={{ fontSize: '1.1em', fontWeight: 800, color: txtPrimary, margin: '16px 0 8px' }}>{children}</h3>,
        p: ({ children }) => <p style={{ margin: '0 0 12px' }}>{children}</p>,
        ul: ({ children }) => <ul style={{ margin: '0 0 12px', paddingLeft: 18 }}>{children}</ul>,
        ol: ({ children }) => <ol style={{ margin: '0 0 12px', paddingLeft: 18 }}>{children}</ol>,
        li: ({ children }) => <li style={{ margin: '0 0 6px' }}>{children}</li>,
        strong: ({ children }) => <strong style={{ fontWeight: 800, color: txtPrimary }}>{children}</strong>,
    };
    const renderJobCard = (job, { showMatchBadge = false } = {}) => {
        const isExpanded = expandedJobId === job.id;
        const cachedDesc = jobDetailCacheRef.current.get(job.id);
        const rawDesc = job.description || cachedDesc || '';
        const cleanedDesc = formatJobMarkdown(rawDesc);
        const previewText = stripMarkdown(cleanedDesc);
        const postedLabel = formatPostedLabel(job.posted_at);
        const applyUrl = cleanLinkedInUrl(job.job_url);
        const appliedLabel = getJobAppliedLabel(job);
        return (
            <div
                key={job.id}
                className="company-job-card"
                style={{
                    borderBottom: `1px solid ${borderComp}`,
                    borderLeft: isExpanded ? `3px solid ${accentColor}` : '3px solid transparent',
                    background: isExpanded ? (isDark ? 'rgba(255,255,255,0.02)' : 'rgba(37,99,235,0.02)') : 'transparent',
                    transition: 'all 0.15s',
                }}
            >
                <button
                    onClick={() => setExpandedJobId(prev => prev === job.id ? null : job.id)}
                    className="company-job-button"
                    style={{
                        width: '100%', textAlign: 'left', background: 'none', border: 'none',
                        cursor: 'pointer', padding: '16px',
                        display: 'flex', alignItems: 'flex-start', gap: 14,
                    }}
                >
                    <div className="company-job-icon" style={{ width: 40, height: 40, borderRadius: 12, background: accentBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: `1px solid ${accentBorder}` }}>
                        <Briefcase size={18} color={accentColor} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                            <p className="company-job-title" style={{ margin: 0, fontSize: 14, fontWeight: 700, color: txtPrimary, lineHeight: 1.3 }}>{job.role}</p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                {showMatchBadge && (
                                    <span className="company-job-badge" style={{ fontSize: 10, fontWeight: 800, color: accentColor, background: accentBg, border: `1px solid ${accentBorder}`, padding: '2px 8px', borderRadius: 999 }}>
                                        Match
                                    </span>
                                )}
                                {appliedLabel && (
                                    <span
                                        className="company-job-badge"
                                        style={{
                                            fontSize: 10,
                                            fontWeight: 800,
                                            color: isDark ? '#34D399' : '#059669',
                                            background: isDark ? 'rgba(52,211,153,0.12)' : 'rgba(16,185,129,0.08)',
                                            border: `1px solid ${isDark ? 'rgba(52,211,153,0.3)' : 'rgba(16,185,129,0.2)'}`,
                                            padding: '2px 8px',
                                            borderRadius: 999
                                        }}
                                    >
                                        {appliedLabel}
                                    </span>
                                )}
                                {postedLabel && (
                                    <span style={{ fontSize: 10, color: txtMuted, fontWeight: 600 }}>{postedLabel}</span>
                                )}
                            </div>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                            {job.job_function && job.job_function !== 'Not Applicable' && (
                                <span style={{ fontSize: 10, color: txtMuted, fontWeight: 600 }}>{job.job_function}</span>
                            )}
                            {job.job_function && job.job_function !== 'Not Applicable' && job.location && <span style={{ color: borderComp }}>•</span>}
                            {job.location && (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, color: txtMuted, fontWeight: 600 }}>
                                    <MapPin size={10} /> {job.location}
                                </span>
                            )}
                        </div>
                    </div>
                    <ChevronDown size={14} color={txtDim} style={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', marginTop: 4 }} />
                </button>
                <AnimatePresence initial={false}>
                    {isExpanded && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.22, ease: 'easeInOut' }}
                            style={{ overflow: 'hidden' }}
                        >
                            <div className="company-job-detail" style={{ padding: '0 16px 20px 70px' }}>
                                {previewText ? (
                                    <div style={{ fontSize: 12, color: txtSecondary, lineHeight: 1.6, marginBottom: 16, whiteSpace: 'pre-wrap' }}>
                                        {previewText.length > 220 ? previewText.slice(0, 220) + '…' : previewText}
                                    </div>
                                ) : jobDetailLoadingId === job.id ? (
                                    <div style={{ marginBottom: 16, fontSize: 12, color: txtDim }}>Loading description…</div>
                                ) : (
                                    <div style={{ marginBottom: 16, fontSize: 12, color: txtDim }}>No description available</div>
                                )}
                                <div style={{ display: 'flex', gap: 10 }}>
                                    {applyUrl && (
                                        <a href={applyUrl} target="_blank" rel="noopener noreferrer"
                                            onClick={() => handleApplyClick(job)}
                                            style={{
                                                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px 12px', borderRadius: 9, fontSize: 12, fontWeight: 800,
                                                background: isDark ? '#F59E0B' : '#2563eb',
                                                color: isDark ? '#1C1917' : '#ffffff',
                                                textDecoration: 'none'
                                            }}>
                                            <Rocket size={12} /> Apply Now
                                        </a>
                                    )}
                                    <button
                                        onClick={() => setSelectedJob(job)}
                                        style={{
                                            padding: '10px 20px', borderRadius: 10, fontSize: 13, fontWeight: 700,
                                            background: isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
                                            color: isDark ? 'rgba(255,255,255,0.8)' : '#334155',
                                            border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        View Details
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        );
    };
    /* ── render ── */
    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        key="backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.22 }}
                        onClick={handleClosePanel}
                        style={{
                            position: 'fixed', inset: 0, zIndex: 200,
                            background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)',
                        }}
                    />
                    {/* Panel */}
                    <motion.div
                        key="panel"
                        initial={{ x: '100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '100%' }}
                        transition={{ type: 'spring', damping: 28, stiffness: 220, mass: 0.8 }}
                        className="company-panel"
                        style={{
                            position: 'fixed', top: 0, right: 0, bottom: 0,
                            width: 'min(520px, 100vw)',
                            zIndex: 201,
                            background: panelBg,
                            borderLeft: `1px solid ${panelBdr}`,
                            display: 'flex',
                            flexDirection: 'column',
                            boxShadow: isDark ? '-24px 0 60px rgba(0,0,0,0.6)' : '-24px 0 60px rgba(59,130,246,0.08)',
                            fontFamily: "'Manrope', sans-serif",
                            color: panelClr,
                        }}
                    >
                        {/* ── Panel Header ── */}
                        <div className="company-panel-header" style={{
                            flexShrink: 0,
                            padding: '10px 18px 8px',
                            borderBottom: isDark ? '1px solid rgba(255,255,255,0.07)' : '1px solid #e8edf8',
                            background: isDark ? 'rgba(255,255,255,0.02)' : '#f8faff',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 4 }}>
                                <div style={{ minWidth: 0 }}>
                                    <h2 className="company-panel-title" style={{ fontSize: 15, fontWeight: 800, color: panelClr, fontFamily: "'Playfair Display', serif", margin: '0 0 3px' }}>
                                        {companyName || 'Company'} – {outerTab === 'jobs' ? 'Open Roles' : 'Employees'}
                                    </h2>
                                    <p className="company-panel-subtitle" style={{ fontSize: 11, color: isDark ? 'rgba(255,255,255,0.4)' : '#64748b', margin: 0 }}>
                                        {outerTab === 'jobs'
                                            ? `Jobs at ${companyName || 'this company'}`
                                            : (startYear && endYear
                                                ? `Alumni who reached ${companyName || 'this company'} in hop ${hop} (${startYear}–${endYear})`
                                                : `Alumni who reached ${companyName || 'this company'} in hop ${hop}`)}
                                    </p>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                                    {/* Close */}
                                    <button
                                        onClick={handleClosePanel}
                                        style={{
                                            width: 28, height: 28, borderRadius: '50%',
                                            background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.04)',
                                            border: `1px solid ${borderComp}`,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            cursor: 'pointer', color: txtSecondary, transition: 'all 0.15s',
                                        }}
                                        onMouseEnter={e => {
                                            e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(15,23,42,0.08)';
                                            e.currentTarget.style.color = txtPrimary;
                                        }}
                                        onMouseLeave={e => {
                                            e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.04)';
                                            e.currentTarget.style.color = txtSecondary;
                                        }}
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                            </div>
                        </div>
                        {/* ── Outer tab bar: Employees | Jobs ── */}
                        <div className="company-panel-tabs" style={{
                            flexShrink: 0,
                            display: 'flex',
                            borderBottom: isDark ? '2px solid rgba(255,255,255,0.07)' : '2px solid #e2e8f0',
                            background: isDark ? 'rgba(0,0,0,0.2)' : '#f8faff',
                        }}>
                            {[
                                { key: 'employees', label: 'Employees' },
                                { key: 'jobs', label: 'Open Roles' },
                            ].map(tab => {
                                const isActive = outerTab === tab.key;
                                return (
                                    <button
                                        key={tab.key}
                                        onClick={() => setOuterTab(tab.key)}
                                        className="company-panel-tab-button"
                                        style={{
                                            flex: 1, padding: '11px 8px', fontSize: 12, fontWeight: 800,
                                            background: 'none', border: 'none', cursor: 'pointer',
                                            color: isActive ? (isDark ? '#F59E0B' : '#2563eb') : (isDark ? 'rgba(255,255,255,0.35)' : '#94a3b8'),
                                            borderBottom: isActive ? `2px solid ${isDark ? '#F59E0B' : '#2563eb'}` : '2px solid transparent',
                                            marginBottom: -2,
                                            transition: 'all 0.15s', letterSpacing: '0.02em',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                                        }}
                                    >
                                        {tab.key === 'employees' ? <UserIcon size={13} /> : <Briefcase size={13} />}
                                        {tab.label}
                                        {tab.key === 'jobs' && !jobsLoading && outerTab === 'jobs' && jobs.length > 0 && (
                                            <span style={{ fontSize: 10, background: isDark ? 'rgba(245,158,11,0.15)' : 'rgba(59,130,246,0.12)', color: isDark ? '#F59E0B' : '#2563eb', padding: '1px 6px', borderRadius: 99, fontWeight: 700 }}>
                                                {jobs.length}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                        {/* ── Jobs tab content ── */}
                        {outerTab === 'jobs' && (
                            <div
                                ref={jobsListRef}
                                onScroll={handleJobsScroll}
                                style={{ flex: 1, overflowY: 'auto' }}
                            >
                                {jobsLoading && (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 0', gap: 12 }}>
                                        <Loader2 size={26} color={isDark ? "#F59E0B" : "#2563eb"} style={{ animation: 'spin 1s linear infinite' }} />
                                        <p style={{ color: isDark ? 'rgba(255,255,255,0.4)' : '#64748b', fontSize: 12, margin: 0 }}>Fetching open roles…</p>
                                    </div>
                                )}
                                {!jobsLoading && jobsError && (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 24px', gap: 10 }}>
                                        <AlertCircle size={26} color="rgba(239,68,68,0.6)" />
                                        <p style={{ color: isDark ? 'rgba(255,255,255,0.45)' : '#64748b', fontSize: 12, margin: 0, textAlign: 'center' }}>{jobsError}</p>
                                    </div>
                                )}
                                {!jobsLoading && !jobsError && jobs.length === 0 && (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 24px', gap: 12 }}>
                                        <div style={{ width: 54, height: 54, borderRadius: 18, background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(15,23,42,0.04)', border: `1px solid ${borderComp}`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: isDark ? '0 8px 24px rgba(0,0,0,0.35)' : '0 8px 24px rgba(0,0,0,0.06)' }}>
                                            <Inbox size={22} color={isDark ? "rgba(255,255,255,0.25)" : "rgba(15,23,42,0.2)"} />
                                        </div>
                                        <p style={{ color: txtPrimary, fontSize: 13, fontWeight: 700, margin: 0, opacity: 0.8 }}>No open roles yet</p>
                                        <p style={{ color: txtMuted, fontSize: 11, margin: 0, textAlign: 'center', lineHeight: 1.6, maxWidth: 320 }}>
                                            We don’t have any job postings for {companyName || 'this company'} right now. Check back later.
                                        </p>
                                    </div>
                                )}
                                {!jobsLoading && !jobsError && jobs.length > 0 && (
                                    <div style={{ paddingTop: 4 }}>
                                        {matchedJobs.length > 0 && (
                                            <div style={{ padding: '0 16px 6px', borderBottom: `1px solid ${borderComp}`, marginTop: 8 }}>
                                                <p style={{ fontSize: 10, fontWeight: 800, color: txtMuted, textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0 }}>
                                                    Matching your profile
                                                </p>
                                            </div>
                                        )}
                                        {matchedJobs.length === 0 && (
                                            <div style={{
                                                margin: '8px 16px 6px',
                                                padding: '10px 12px',
                                                borderRadius: 10,
                                                background: isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc',
                                                border: `1px solid ${borderComp}`,
                                                color: txtSecondary,
                                                fontSize: 11,
                                                lineHeight: 1.5,
                                            }}>
                                                <div style={{ fontWeight: 700, color: isDark ? 'rgba(255,255,255,0.75)' : '#0f172a', marginBottom: 2 }}>No roles match your current profile</div>
                                                We couldn’t find matches for your current skills or roles. Showing all open roles below.
                                            </div>
                                        )}
                                        {matchedJobs.map((job) => renderJobCard(job, { showMatchBadge: true }))}
                                        {otherJobs.length > 0 && (
                                            <div style={{ padding: '12px 16px 6px', borderBottom: `1px solid ${borderComp}` }}>
                                                <p style={{ fontSize: 10, fontWeight: 800, color: txtMuted, textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0 }}>
                                                    Other open roles
                                                </p>
                                            </div>
                                        )}
                                        {otherJobs.map((job) => renderJobCard(job))}
                                        {jobsHasMore && (
                                            <div style={{ padding: '12px 16px 20px', textAlign: 'center', color: 'rgba(255,255,255,0.35)', fontSize: 11 }}>
                                                Scroll to load more roles
                                            </div>
                                        )}
                                        {jobsLoadingMore && (
                                            <div style={{ padding: '4px 0 18px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: isDark ? 'rgba(255,255,255,0.35)' : '#94a3b8', fontSize: 11 }}>
                                                <Loader2 size={14} style={{ color: accentColor, animation: 'spin 1s linear infinite' }} />
                                                Loading more roles…
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                        {/* ── Employees tab content guard ── */}
                        {outerTab === 'employees' && (<Fragment>
                            {/* Hop tabs + total */}
                            {(hasTransitionSource || !isFromJobsPage) && (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    flexWrap: 'wrap',
                                    padding: '6px 16px 10px',
                                    borderBottom: `1px solid ${borderComp}`,
                                    background: bgComp,
                                }}>
                                    {hasTransitionSource && (
                                        <>
                                            <div style={{ display: 'flex', gap: 4, background: isDark ? 'rgba(255,255,255,0.04)' : '#ffffff', padding: 3, borderRadius: 9, border: `1px solid ${borderComp}` }}>
                                                {[
                                                    { val: 1, label: '1st transition' },
                                                    { val: 2, label: '2nd transition' },
                                                    { val: 3, label: '3rd transition' },
                                                ].map(t => {
                                                    const isActive = hop === t.val;
                                                    const peopleInHop = allHops[t.val] || [];
                                                    const hasPeople = peopleInHop.length > 0;
                                                    const hasMatch = peopleInHop.some(p =>
                                                        p.is_match ||
                                                        crossMatchedIdSet.has(Number(p.employee_id)) ||
                                                        (normalizedRoleFilter.length > 0 && isRoleMatchedForPerson(p)) ||
                                                        isLocationMatchedForPerson(p)
                                                    );
                                                    return (
                                                        <button
                                                            key={t.val}
                                                            onClick={() => handleHopChange(t.val)}
                                                            style={{
                                                                padding: '5px 12px', borderRadius: 7, fontSize: 11,
                                                                fontWeight: isActive ? 800 : 600,
                                                                background: isActive ? accentColor : 'transparent',
                                                                color: isActive ? (isDark ? '#1C1917' : '#ffffff') : (hasMatch ? accentColor : (hasPeople ? txtSecondary : txtDim)),
                                                                border: 'none', cursor: 'pointer', transition: 'all 0.15s',
                                                                display: 'flex', alignItems: 'center', gap: 4,
                                                            }}
                                                        >
                                                            {t.label}
                                                            {hasPeople && !isActive && (
                                                                <span style={{ fontSize: 10, opacity: 0.6, marginLeft: 1 }}>{peopleInHop.length}</span>
                                                            )}
                                                            {hasMatch && !isActive && (
                                                                <motion.div
                                                                    initial={{ opacity: 0.6, scale: 0.8 }}
                                                                    animate={{
                                                                        opacity: [0.6, 1, 0.6],
                                                                        scale: [0.8, 1.4, 0.8],
                                                                        boxShadow: [
                                                                            `0 0 4px ${accentColor}`,
                                                                            `0 0 12px ${accentColor}`,
                                                                            `0 0 4px ${accentColor}`
                                                                        ]
                                                                    }}
                                                                    transition={{
                                                                        duration: 2,
                                                                        repeat: Infinity,
                                                                        ease: "easeInOut"
                                                                    }}
                                                                    style={{
                                                                        width: 5,
                                                                        height: 5,
                                                                        borderRadius: '50%',
                                                                        background: accentColor,
                                                                        flexShrink: 0,
                                                                        marginLeft: 2
                                                                    }}
                                                                />
                                                            )}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            <div
                                                style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}
                                                onMouseEnter={() => setShowTransitionInfo(true)}
                                                onMouseLeave={() => setShowTransitionInfo(false)}
                                            >
                                                <button
                                                    type="button"
                                                    aria-label="Transition info"
                                                    style={{
                                                        width: 18,
                                                        height: 18,
                                                        borderRadius: '999px',
                                                        border: `1px solid ${borderComp}`,
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        background: isDark ? 'rgba(255,255,255,0.05)' : '#ffffff',
                                                        color: txtMuted,
                                                        cursor: 'pointer',
                                                        padding: 0,
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    <span style={{ fontSize: 11, fontWeight: 700, lineHeight: 1 }}>i</span>
                                                </button>
                                                {showTransitionInfo && (
                                                    <div
                                                        style={{
                                                            position: 'absolute',
                                                            top: 24,
                                                            right: 0,
                                                            width: 360,
                                                            maxWidth: 'calc(100vw - 48px)',
                                                            zIndex: 40,
                                                            borderRadius: 10,
                                                            padding: '10px 12px',
                                                            background: panelBg,
                                                            color: txtSecondary,
                                                            border: `1px solid ${borderComp}`,
                                                            boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
                                                            fontSize: 11,
                                                            lineHeight: 1.5,
                                                        }}
                                                    >
                                                        <div>
                                                            <strong>1st transition:</strong>{' '}
                                                            Moved from {sourceCompanyName || 'the source company'} to {companyName || 'this company'} in their very next job.
                                                        </div>
                                                        <div style={{ marginTop: 6 }}>
                                                            <strong>2nd transition:</strong>{' '}
                                                            Moved to {companyName || 'this company'} after working at one other company after {sourceCompanyName || 'the source company'}.
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    )}
                                    {!isFromJobsPage && (
                                        <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 700, color: accentColor }}>
                                            Total <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 14 }}>{totalEmployees}</span>
                                        </span>
                                    )}
                                </div>
                            )}
                            {hopNudge && <p style={{ fontSize: 11, color: accentColor, margin: '8px 16px' }}>{hopNudge}</p>}
                            {/* ── Search / filter bar ── */}
                            <div style={{
                                flexShrink: 0,
                                padding: '8px 16px',
                                borderBottom: `1px solid ${borderComp}`,
                                background: isDark ? 'rgba(0,0,0,0.15)' : '#ffffff',
                            }}>
                                <div style={{ position: 'relative' }}>
                                    <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: txtDim }} />
                                    <input
                                        type="text"
                                        value={roleFilter}
                                        onChange={(e) => setRoleFilter(e.target.value)}
                                        placeholder="Filter by name or role..."
                                        style={{
                                            width: '100%', padding: '7px 12px 7px 36px', borderRadius: 9,
                                            background: isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9',
                                            border: `1px solid ${borderComp}`,
                                            color: txtPrimary, fontSize: 12, outline: 'none',
                                            boxSizing: 'border-box',
                                            transition: 'all 0.2s',
                                        }}
                                        onFocus={e => e.currentTarget.style.borderColor = accentColor}
                                        onBlur={e => e.currentTarget.style.borderColor = borderComp}
                                    />
                                </div>
                            </div>
                            {/* ── Best people to contact ── */}
                            <div style={{
                                flexShrink: 0,
                                padding: '8px 16px',
                                borderBottom: `1px solid ${borderComp}`,
                                background: bgComp,
                            }}>
                                <button
                                    onClick={handleToggleBestPeople}
                                    style={{
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 10,
                                        background: accentBg,
                                        border: `1px solid ${accentBorder}`,
                                        borderRadius: 12,
                                        padding: '8px 14px',
                                        color: accentColor,
                                        fontWeight: 800,
                                        fontSize: 11,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.08em',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.05)'}
                                    onMouseLeave={e => e.currentTarget.style.filter = 'none'}
                                >
                                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: accentColor, boxShadow: `0 0 8px ${accentColor}` }} />
                                    <span style={{ flex: 1, textAlign: 'left' }}>Best People to Contact</span>
                                    {!dismissedNewTags.best_people_to_contact && (
                                        <FeatureNewBadge
                                            isDark={isDark}
                                            tooltip="Best People to Contact sorted by likelihood to give referral."
                                        />
                                    )}
                                    <span style={{ fontSize: 11, fontWeight: 700, color: txtSecondary, textTransform: 'none', letterSpacing: '0' }}>
                                        {bestPeopleCountReady ? `${bestPeopleCount} matches` : ' '}
                                    </span>
                                    {bestPeopleOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                </button>
                                {bestPeopleOpen && (
                                    <TooltipProvider delayDuration={120}>
                                    <div style={{
                                        marginTop: 10,
                                        borderRadius: 12,
                                        border: isDark ? '1px solid rgba(245,158,11,0.25)' : '1px solid rgba(37,99,235,0.22)',
                                        padding: '10px 12px',
                                        background: isDark ? 'rgba(255,255,255,0.02)' : '#f8faff',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 10,
                                        maxHeight: 360,
                                        overflowY: 'auto',
                                    }}>
                                        {bestPeopleLoading && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: txtMuted, fontSize: 12 }}>
                                                <Loader2 className="w-4 h-4 animate-spin" style={{ color: accentColor }} />
                                                Loading best people…
                                            </div>
                                        )}
                                        {!bestPeopleLoading && (
                                            <>
                                                {bestPeople?.access_mode === 'no_link' && (
                                                    <div style={{ fontSize: 11, color: txtSecondary, background: isDark ? 'rgba(245,158,11,0.08)' : 'rgba(37,99,235,0.07)', border: `1px solid ${accentBorder}`, borderRadius: 10, padding: '8px 10px' }}>
                                                        <div>Upgrade to Premium and get a direct link to this person's LinkedIn profile</div>
                                                        <button
                                                            type="button"
                                                            onClick={() => navigate('/pricing', { state: { ...pricingNavState, from: 'best_people_link_lock' } })}
                                                            style={{ marginTop: 6, padding: '5px 9px', borderRadius: 7, border: `1px solid ${accentColor}`, background: 'transparent', color: accentColor, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                                                        >
                                                            Get the direct link
                                                        </button>
                                                    </div>
                                                )}
                                                {bestPeople?.access_mode === 'masked' && (
                                                    <div style={{ fontSize: 11, color: txtSecondary, background: isDark ? 'rgba(245,158,11,0.08)' : 'rgba(37,99,235,0.07)', border: `1px solid ${accentBorder}`, borderRadius: 10, padding: '8px 10px' }}>
                                                        <div>See their work history for free. Upgrade to see who it belongs to and reach them directly.</div>
                                                        <button
                                                            type="button"
                                                            onClick={() => navigate('/pricing', { state: { ...pricingNavState, from: 'best_people_identity_lock' } })}
                                                            style={{ marginTop: 6, padding: '5px 9px', borderRadius: 7, border: `1px solid ${accentColor}`, background: 'transparent', color: accentColor, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                                                        >
                                                            See Premium Benefits
                                                        </button>
                                                    </div>
                                                )}
                                                {bestPeopleWork.length > 0 && (
                                                    <>
                                                        <div style={{ fontSize: 10, fontWeight: 800, color: txtMuted, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                                                            Company Alumni
                                                        </div>
                                                        {bestPeopleWork.map((p) => {
                                                            const tierMeta = getBestPeopleTierMeta('work', p.tier);
                                                            const initials = getInitials(p.employee_name || '');
                                                            const color = avatarColor(p.employee_name || '');
                                                            const isOpen = Number(p.employee_id) === bestExpandedId;
                                                            const entry = peopleIndex.get(Number(p.employee_id));
                                                            const person = entry?.person;
                                                            const cacheKey = entry?.source === 'transition'
                                                                ? `transition:${entry?.hop || hop}:${p.employee_id}`
                                                                : `related:${p.employee_id}`;
                                                            const cachedDetail = detailCacheRef.current.get(cacheKey);
                                                            const personWithDetail = cachedDetail && person ? { ...person, ...cachedDetail } : person;
                                                            return (
                                                                <div key={`best-work-${p.employee_id}`} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                                    <button
                                                                        onClick={() => handleBestPersonClick(p.employee_id)}
                                                                        style={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: 10,
                                                                            width: '100%',
                                                                            padding: '8px 10px',
                                                                            borderRadius: 10,
                                                                            background: isDark ? 'rgba(255,255,255,0.03)' : '#f1f5f9',
                                                                            border: `1px solid ${borderComp}`,
                                                                            color: txtPrimary,
                                                                            cursor: 'pointer',
                                                                            textAlign: 'left',
                                                                        }}
                                                                    >
                                                                        <span style={{
                                                                            width: 28, height: 28, borderRadius: '50%',
                                                                            background: `${color}25`, color,
                                                                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                                            fontSize: 11, fontWeight: 800,
                                                                            flexShrink: 0,
                                                                        }}>
                                                                            {initials}
                                                                        </span>
                                                                        <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3, overflow: 'hidden' }}>
                                                                            <span style={{ fontSize: 13, fontWeight: 700, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                                <span style={{ filter: p.identity_hidden ? 'blur(8px)' : 'none', userSelect: p.identity_hidden ? 'none' : 'auto' }}>
                                                                                    {p.employee_name || `Profile ${p.employee_id}`}
                                                                                </span>
                                                                            </span>
                                                                            {tierMeta && (
                                                                                <TierChip
                                                                                    label={tierMeta.label}
                                                                                    tooltip={`${tierMeta.key}: ${tierMeta.label}`}
                                                                                />
                                                                            )}
                                                                        </span>
                                                                        <ChevronRight size={14} style={{ color: txtDim, flexShrink: 0, transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }} />
                                                                    </button>
                                                                    {isOpen && personWithDetail && (
                                                                        <div style={{ padding: '0 4px 4px' }}>
                                                                            <PersonDetail
                                                                                person={personWithDetail}
                                                                                isTransition={entry?.source === 'transition'}
                                                                                linkedInUrl={(personWithDetail.linkedin_url) || buildLinkedInSearchUrl(
                                                                                    personWithDetail.employee_name || `Employee ${p.employee_id}`,
                                                                                    getTopCompanyNames(personWithDetail.experience_history)
                                                                                )}
                                                                                detailLoading={detailLoadingId === p.employee_id}
                                                                                detailError={detailError}
                                                                                maskIdentity={!!p.identity_hidden}
                                                                                hideLinkedIn={!!p.link_hidden}
                                                                            />
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </>
                                                )}
                                                {bestPeopleCollege.length > 0 && (
                                                    <>
                                                        <div style={{ fontSize: 10, fontWeight: 800, color: txtMuted, letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: bestPeopleWork.length > 0 ? 6 : 0 }}>
                                                            College Alumni
                                                        </div>
                                                        {bestPeopleCollege.map((p) => {
                                                            const tierMeta = getBestPeopleTierMeta('college', p.tier);
                                                            const initials = getInitials(p.employee_name || '');
                                                            const color = avatarColor(p.employee_name || '');
                                                            const isOpen = Number(p.employee_id) === bestExpandedId;
                                                            const entry = peopleIndex.get(Number(p.employee_id));
                                                            const person = entry?.person;
                                                            const cacheKey = entry?.source === 'transition'
                                                                ? `transition:${entry?.hop || hop}:${p.employee_id}`
                                                                : `related:${p.employee_id}`;
                                                            const cachedDetail = detailCacheRef.current.get(cacheKey);
                                                            const personWithDetail = cachedDetail && person ? { ...person, ...cachedDetail } : person;
                                                            return (
                                                                <div key={`best-college-${p.employee_id}`} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                                    <button
                                                                        onClick={() => handleBestPersonClick(p.employee_id)}
                                                                        style={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: 10,
                                                                            width: '100%',
                                                                            padding: '8px 10px',
                                                                            borderRadius: 10,
                                                                            background: isDark ? 'rgba(255,255,255,0.03)' : '#f1f5f9',
                                                                            border: `1px solid ${borderComp}`,
                                                                            color: txtPrimary,
                                                                            cursor: 'pointer',
                                                                            textAlign: 'left',
                                                                        }}
                                                                    >
                                                                        <span style={{
                                                                            width: 28, height: 28, borderRadius: '50%',
                                                                            background: `${color}25`, color,
                                                                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                                            fontSize: 11, fontWeight: 800,
                                                                            flexShrink: 0,
                                                                        }}>
                                                                            {initials}
                                                                        </span>
                                                                        <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3, overflow: 'hidden' }}>
                                                                            <span style={{ fontSize: 13, fontWeight: 700, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                                <span style={{ filter: p.identity_hidden ? 'blur(8px)' : 'none', userSelect: p.identity_hidden ? 'none' : 'auto' }}>
                                                                                    {p.employee_name || `Profile ${p.employee_id}`}
                                                                                </span>
                                                                            </span>
                                                                            {tierMeta && (
                                                                                <TierChip
                                                                                    label={tierMeta.label}
                                                                                    tooltip={`${tierMeta.key}: ${tierMeta.label}`}
                                                                                />
                                                                            )}
                                                                        </span>
                                                                        <ChevronRight size={14} style={{ color: txtDim, flexShrink: 0, transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }} />
                                                                    </button>
                                                                    {isOpen && personWithDetail && (
                                                                        <div style={{ padding: '0 4px 4px' }}>
                                                                            <PersonDetail
                                                                                person={personWithDetail}
                                                                                isTransition={entry?.source === 'transition'}
                                                                                linkedInUrl={(personWithDetail.linkedin_url) || buildLinkedInSearchUrl(
                                                                                    personWithDetail.employee_name || `Employee ${p.employee_id}`,
                                                                                    getTopCompanyNames(personWithDetail.experience_history)
                                                                                )}
                                                                                detailLoading={detailLoadingId === p.employee_id}
                                                                                detailError={detailError}
                                                                                maskIdentity={!!p.identity_hidden}
                                                                                hideLinkedIn={!!p.link_hidden}
                                                                            />
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </>
                                                )}
                                                {bestPeopleRecruiters.length > 0 && (
                                                    <>
                                                        <div style={{ fontSize: 10, fontWeight: 800, color: txtMuted, letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: (bestPeopleWork.length > 0 || bestPeopleCollege.length > 0) ? 6 : 0 }}>
                                                            Recruiters
                                                        </div>
                                                        {bestPeopleRecruiters.map((p) => {
                                                            const initials = getInitials(p.employee_name || '');
                                                            const color = avatarColor(p.employee_name || '');
                                                            const isOpen = Number(p.employee_id) === bestExpandedId;
                                                            const entry = peopleIndex.get(Number(p.employee_id));
                                                            const person = entry?.person;
                                                            const cacheKey = entry?.source === 'transition'
                                                                ? `transition:${entry?.hop || hop}:${p.employee_id}`
                                                                : `related:${p.employee_id}`;
                                                            const cachedDetail = detailCacheRef.current.get(cacheKey);
                                                            const personWithDetail = cachedDetail && person ? { ...person, ...cachedDetail } : person;
                                                            return (
                                                                <div key={`best-recruiter-${p.employee_id}`} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                                    <button
                                                                        onClick={() => handleBestPersonClick(p.employee_id)}
                                                                        style={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: 10,
                                                                            width: '100%',
                                                                            padding: '8px 10px',
                                                                            borderRadius: 10,
                                                                            background: isDark ? 'rgba(255,255,255,0.03)' : '#f1f5f9',
                                                                            border: `1px solid ${borderComp}`,
                                                                            color: txtPrimary,
                                                                            cursor: 'pointer',
                                                                            textAlign: 'left',
                                                                        }}
                                                                    >
                                                                        <span style={{
                                                                            width: 28, height: 28, borderRadius: '50%',
                                                                            background: `${color}25`, color,
                                                                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                                            fontSize: 11, fontWeight: 800,
                                                                            flexShrink: 0,
                                                                        }}>
                                                                            {initials}
                                                                        </span>
                                                                        <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3, overflow: 'hidden' }}>
                                                                            <span style={{ fontSize: 13, fontWeight: 700, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                                <span style={{ filter: p.identity_hidden ? 'blur(8px)' : 'none', userSelect: p.identity_hidden ? 'none' : 'auto' }}>
                                                                                    {p.employee_name || `Profile ${p.employee_id}`}
                                                                                </span>
                                                                            </span>
                                                                        </span>
                                                                        <ChevronRight size={14} style={{ color: txtDim, flexShrink: 0, transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }} />
                                                                    </button>
                                                                    {isOpen && personWithDetail && (
                                                                        <div style={{ padding: '0 4px 4px' }}>
                                                                            <PersonDetail
                                                                                person={personWithDetail}
                                                                                isTransition={entry?.source === 'transition'}
                                                                                linkedInUrl={(personWithDetail.linkedin_url) || buildLinkedInSearchUrl(
                                                                                    personWithDetail.employee_name || `Employee ${p.employee_id}`,
                                                                                    getTopCompanyNames(personWithDetail.experience_history)
                                                                                )}
                                                                                detailLoading={detailLoadingId === p.employee_id}
                                                                                detailError={detailError}
                                                                                maskIdentity={!!p.identity_hidden}
                                                                                hideLinkedIn={!!p.link_hidden}
                                                                            />
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </div>
                                    </TooltipProvider>
                                )}
                            </div>
                            {/* ── People tabs ── */}
                            <div style={{
                                flexShrink: 0,
                                display: 'flex',
                                borderBottom: `1px solid ${borderComp}`,
                                background: bgComp,
                            }}>
                                {[
                                    ...(hasTransitionSource ? [{ key: 'transition', label: 'Transition People' }] : []),
                                    { key: 'related', label: 'Related to Your Background' },
                                ].map(tab => (
                                    <button
                                        key={tab.key}
                                        onClick={() => handlePeopleTabChange(tab.key)}
                                        style={{
                                            flex: 1, padding: '9px 8px', fontSize: 11, fontWeight: 700,
                                            background: 'none', border: 'none', cursor: 'pointer',
                                            color: peopleTab === tab.key ? accentColor : txtMuted,
                                            borderBottom: peopleTab === tab.key ? `2px solid ${accentColor}` : '2px solid transparent',
                                            transition: 'all 0.15s',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {tab.label}
                                        {tab.key === 'related' && (loadingRelated || profileStatus === 'loading') && (
                                            <Loader2 size={10} style={{ marginLeft: 4, display: 'inline', animation: 'spin 1s linear infinite' }} />
                                        )}
                                        {tab.key === 'related' && showRelatedMatchCue && (
                                            <span style={{
                                                marginLeft: 6, fontSize: 8, fontWeight: 800, padding: '2px 6px',
                                                borderRadius: 99, background: accentColor, color: isDark ? '#1C1917' : '#ffffff',
                                                boxShadow: `0 0 8px ${accentColor}66`, verticalAlign: 'middle',
                                                display: 'inline-flex', alignItems: 'center'
                                            }}>
                                                MATCHES
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div>
                            {/* Sorted by label */}
                            <div style={{ padding: '8px 16px 6px', borderBottom: `1px solid ${borderComp}` }}>
                                <p style={{ fontSize: 9, fontWeight: 800, color: txtDim, textTransform: 'uppercase', letterSpacing: '0.12em', margin: 0 }}>
                                    SORTED BY LIKELINESS TO MATCH
                                </p>
                            </div>
                            {/* ── People list (scrollable) ── */}
                            <div style={{ flex: 1, overflowY: 'auto' }}>
                                {error && (
                                    <div style={{ padding: 20, color: '#FCA5A5', fontSize: 12 }}>{error}</div>
                                )}
                                {peopleTab === 'transition' && (
                                    <>
                                        {loading ? (
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 60 }}>
                                                <Loader2 style={{ width: 28, height: 28, color: accentColor, animation: 'spin 1s linear infinite' }} />
                                            </div>
                                        ) : filteredEmployees.length === 0 ? (
                                            <div style={{ padding: 20, fontSize: 12, color: 'rgba(255,255,255,0.3)', lineHeight: 1.6 }}>
                                                {transitionEmptyMessage}
                                            </div>
                                        ) : (
                                            filteredEmployees.map((emp) => (
                                                <PersonRow key={emp.employee_id} person={emp} isTransition={true} />
                                            ))
                                        )}
                                    </>
                                )}
                                {peopleTab === 'related' && (
                                    <>
                                        {(loadingRelated || profileStatus === 'loading') ? (
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 60 }}>
                                                <Loader2 style={{ width: 28, height: 28, color: accentColor, animation: 'spin 1s linear infinite' }} />
                                            </div>
                                        ) : profileStatus === 'missing' ? (
                                            <div style={{ padding: 24, textAlign: 'center' }}>
                                                <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(245,158,11,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                                                    <UserIcon style={{ width: 20, height: 20, color: '#F59E0B' }} />
                                                </div>
                                                <p style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.85)', marginBottom: 6 }}>Complete your profile</p>
                                                <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 14, lineHeight: 1.5 }}>Add work or education details to see people from your past network.</p>
                                                <button
                                                    onClick={() => { onClose(); navigate('/profile-setup'); }}
                                                    style={{ padding: '7px 18px', borderRadius: 99, background: 'rgba(245,158,11,0.1)', color: '#F59E0B', fontSize: 11, fontWeight: 700, border: '1px solid rgba(245,158,11,0.2)', cursor: 'pointer' }}
                                                >
                                                    Setup Profile
                                                </button>
                                            </div>
                                        ) : profileStatus === 'error' ? (
                                            <div style={{ padding: 20, fontSize: 11, color: 'rgba(255,255,255,0.3)', textAlign: 'center' }}>Unable to load profile data.</div>
                                        ) : filteredRelatedBackground.length === 0 ? (
                                            <div style={{ padding: 20, fontSize: 12, color: 'rgba(255,255,255,0.3)', lineHeight: 1.6 }}>
                                                {relatedEmptyMessage}
                                            </div>
                                        ) : (
                                            filteredRelatedBackground.map((person) => (
                                                <PersonRow key={person.employee_id} person={person} isTransition={false} />
                                            ))
                                        )}
                                    </>
                                )}
                            </div>
                            {/* Show only matches toggle at the bottom */}
                            {hasMatchesToggle && (
                                <div style={{
                                    flexShrink: 0, padding: '10px 16px',
                                    borderTop: '1px solid rgba(255,255,255,0.07)',
                                    display: 'flex', alignItems: 'center', gap: 8,
                                    background: 'rgba(0,0,0,0.1)',
                                }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
                                        <div
                                            onClick={() => setShowOnlyMatched(v => !v)}
                                            style={{
                                                width: 34, height: 18, borderRadius: 99,
                                                background: showOnlyMatched ? (isDark ? 'rgba(96,165,250,0.6)' : accentColor) : (isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'),
                                                position: 'relative', cursor: 'pointer', transition: 'background 0.2s',
                                            }}
                                        >
                                            <div style={{
                                                position: 'absolute', top: 2, left: showOnlyMatched ? 18 : 2,
                                                width: 14, height: 14, borderRadius: '50%', background: '#fff',
                                                transition: 'left 0.2s',
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                                            }} />
                                        </div>
                                        <span style={{ fontSize: 11, color: showOnlyMatched ? txtPrimary : txtMuted }}>
                                            Show only matches
                                        </span>
                                        <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: isDark ? 'rgba(96,165,250,0.1)' : accentBg, color: isDark ? '#60A5FA' : accentColor, border: `1px solid ${isDark ? 'rgba(96,165,250,0.2)' : accentBorder}`, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                                            Filters Active
                                        </span>
                                    </label>
                                </div>
                            )}
                        </Fragment>)}
                        {/* Job Detail Overlay */}
                        <AnimatePresence>
                            {selectedJob && (
                                <motion.div
                                    key="job-overlay"
                                    initial={{ x: '100%' }}
                                    animate={{ x: 0 }}
                                    exit={{ x: '100%' }}
                                    transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                                    className="job-overlay"
                                    style={{
                                        position: 'absolute', top: 0, right: 0, bottom: 0, left: 0,
                                        zIndex: 300, background: panelBg, display: 'flex', flexDirection: 'column',
                                    }}
                                >
                                    {/* Overlay Header */}
                                    <div className="job-overlay-header" style={{ flexShrink: 0, padding: '20px 24px', borderBottom: `1px solid ${borderComp}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: bgComp }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                            <div className="job-overlay-logo" style={{ width: 32, height: 32, borderRadius: 8, background: accentBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 900, color: accentColor }}>
                                                {companyName?.[0] || 'Z'}
                                            </div>
                                            <span className="job-overlay-company" style={{ fontSize: 15, fontWeight: 800, color: txtPrimary }}>{companyName || 'Zepto'}</span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                            <button
                                                onClick={() => setSelectedJob(null)}
                                                style={{
                                                    width: 30, height: 30, borderRadius: '50%',
                                                    background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.04)',
                                                    border: `1px solid ${borderComp}`,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    cursor: 'pointer', color: txtSecondary,
                                                    transition: 'all 0.2s',
                                                }}
                                                onMouseEnter={e => {
                                                    e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(15,23,42,0.08)';
                                                    e.currentTarget.style.transform = 'rotate(90deg)';
                                                }}
                                                onMouseLeave={e => {
                                                    e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.04)';
                                                    e.currentTarget.style.transform = 'rotate(0deg)';
                                                }}
                                            >
                                                <X size={16} />
                                            </button>
                                        </div>
                                    </div>
                                    {/* Overlay Content */}
                                    <div className="custom-scrollbar job-overlay-content" style={{ flex: 1, overflowY: 'auto', padding: '32px 24px' }}>
                                        <h1 className="job-overlay-title" style={{ fontSize: 24, fontWeight: 800, color: txtPrimary, marginBottom: 16, lineHeight: 1.2 }}>{selectedJob.role}</h1>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 32 }}>
                                            {[
                                                { icon: <MapPin size={12} />, label: selectedJob.location, color: isDark ? '#60A5FA' : '#2563eb', bg: isDark ? 'rgba(96,165,250,0.1)' : 'rgba(37,99,235,0.08)' },
                                                { icon: <Briefcase size={12} />, label: selectedJob.employment_type || 'Full-time', color: isDark ? '#34D399' : '#059669', bg: isDark ? 'rgba(52,211,153,0.1)' : 'rgba(5,150,105,0.08)' },
                                                { icon: <UserIcon size={12} />, label: selectedJob.seniority || 'Senior', color: isDark ? '#A78BFA' : '#7c3aed', bg: isDark ? 'rgba(167,139,250,0.1)' : 'rgba(124,58,237,0.08)' },
                                                { icon: <FileText size={12} />, label: selectedJob.job_function || 'Engineering', color: isDark ? '#FCD34D' : '#d97706', bg: isDark ? 'rgba(252,211,77,0.08)' : 'rgba(217,119,6,0.08)' },
                                                { icon: <Clock size={12} />, label: formatPostedLabel(selectedJob.posted_at), color: txtMuted, bg: isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9' },
                                            ].filter(i => i.label && i.label !== 'Not Applicable').map((item, i) => (
                                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 99, background: item.bg, border: `1px solid ${item.color}25`, fontSize: 11, fontWeight: 700, color: item.color }}>
                                                    {item.icon} {item.label}
                                                </div>
                                            ))}
                                        </div>
                                        {(() => {
                                            const selectedAppliedLabel = getJobAppliedLabel(selectedJob);
                                            if (!selectedAppliedLabel) return null;
                                            return (
                                                <div style={{ marginTop: -16, marginBottom: 20 }}>
                                                    <span
                                                        style={{
                                                            fontSize: 11,
                                                            fontWeight: 800,
                                                            color: isDark ? '#34D399' : '#059669',
                                                            background: isDark ? 'rgba(52,211,153,0.12)' : 'rgba(16,185,129,0.08)',
                                                            border: `1px solid ${isDark ? 'rgba(52,211,153,0.3)' : 'rgba(16,185,129,0.2)'}`,
                                                            borderRadius: 999,
                                                            padding: '4px 10px',
                                                            display: 'inline-flex'
                                                        }}
                                                    >
                                                        {selectedAppliedLabel}
                                                    </span>
                                                </div>
                                            );
                                        })()}
                                        <div className="job-overlay-actions" style={{ display: 'flex', gap: 12, marginBottom: 40 }}>
                                            <a
                                                href={cleanLinkedInUrl(selectedJob.job_url)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={() => handleApplyClick(selectedJob)}
                                                style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px 14px', borderRadius: 10, background: accentColor, color: isDark ? '#1C1917' : '#ffffff', fontSize: 13, fontWeight: 800, textDecoration: 'none', boxShadow: `0 4px 16px ${accentColor}40` }}>
                                                <Rocket size={14} /> Apply Now
                                            </a>
                                            <button style={{ width: 48, height: 48, borderRadius: 12, background: isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc', border: `1px solid ${borderComp}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: txtMuted, cursor: 'pointer' }}><Bookmark size={20} /></button>
                                            <button style={{ width: 48, height: 48, borderRadius: 12, background: isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc', border: `1px solid ${borderComp}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: txtMuted, cursor: 'pointer' }}><Share2 size={20} /></button>
                                        </div>
                                        <div className="job-markdown-content" style={{ fontSize: 14, color: txtSecondary, lineHeight: 1.8 }}>
                                            {(() => {
                                                const cached = jobDetailCacheRef.current.get(selectedJob.id);
                                                const desc = selectedJob.description || cached || '';
                                                if (!desc) {
                                                    return <div style={{ fontSize: 12, color: txtDim }}>Loading description…</div>;
                                                }
                                                return (
                                                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                                                        {formatJobMarkdown(desc)}
                                                    </ReactMarkdown>
                                                );
                                            })()}
                                        </div>
                                        <div className="job-overlay-role-details" style={{ marginTop: 48, padding: '32px 0', borderTop: `1px solid ${borderComp}` }}>
                                            <h3 style={{ fontSize: 12, fontWeight: 900, color: txtDim, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 24 }}>Role Details</h3>
                                            <div className="job-overlay-role-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                                {[
                                                    { label: 'Location', value: selectedJob.location },
                                                    { label: 'Employment', value: selectedJob.employment_type || 'Full-time' },
                                                    { label: 'Seniority', value: selectedJob.seniority || 'Mid-Senior level' },
                                                    { label: 'Function', value: selectedJob.job_function || 'Information Technology' }
                                                ].map((d, i) => (
                                                    <div key={i} style={{ padding: '16px', borderRadius: 16, background: isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc', border: `1px solid ${borderComp}` }}>
                                                        <p style={{ margin: 0, fontSize: 10, fontWeight: 700, color: txtMuted, textTransform: 'uppercase', marginBottom: 4 }}>{d.label}</p>
                                                        <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: txtPrimary }}>{d.value || 'Not specified'}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                        {jobs.length > 1 && (
                                            <div style={{ marginTop: 32 }}>
                                                <h3 style={{ fontSize: 12, fontWeight: 900, color: txtDim, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>Similar Roles at {companyName || 'Company'}</h3>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                                    {jobs.filter(j => j.id !== selectedJob.id).slice(0, 3).map(other => (
                                                        <button
                                                            key={other.id}
                                                            onClick={() => setSelectedJob(other)}
                                                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderRadius: 12, background: isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc', border: `1px solid ${borderComp}`, cursor: 'pointer', textAlign: 'left', width: '100%' }}
                                                        >
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                                <Briefcase size={14} color={txtMuted} />
                                                                <span style={{ fontSize: 13, fontWeight: 700, color: txtPrimary }}>{other.role}</span>
                                                            </div>
                                                            <span style={{ fontSize: 11, color: txtMuted }}>{other.location}</span>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>
                    <DidYouApplyModal
                        isOpen={showApplyModal}
                        onClose={() => {
                            setShowApplyModal(false);
                            setPendingApplyJob(null);
                        }}
                        onConfirm={handleApplyConfirm}
                        jobTitle={pendingApplyJob?.role}
                        companyName={pendingApplyJob?.companyName || companyName}
                    />
                    <style>{`
                        @media (max-width: 720px) {
                          .company-panel {
                            width: 100vw !important;
                            border-left: none !important;
                          }
                          .company-panel-header {
                            padding: 12px 14px 10px !important;
                          }
                          .company-panel-title {
                            font-size: 14px !important;
                          }
                          .company-panel-subtitle {
                            font-size: 10px !important;
                          }
                          .company-panel-tabs {
                            padding: 0 6px !important;
                          }
                          .company-panel-tab-button {
                            font-size: 11px !important;
                            padding: 10px 6px !important;
                          }
                          .company-job-button {
                            padding: 12px !important;
                            gap: 10px !important;
                          }
                          .company-job-icon {
                            width: 34px !important;
                            height: 34px !important;
                            border-radius: 10px !important;
                          }
                          .company-job-title {
                            font-size: 13px !important;
                          }
                          .company-job-badge {
                            font-size: 9px !important;
                            padding: 2px 5px !important;
                          }
                          .company-job-detail {
                            padding: 0 12px 16px 56px !important;
                          }
                          .job-overlay-header {
                            padding: 14px 16px !important;
                          }
                          .job-overlay-logo {
                            width: 28px !important;
                            height: 28px !important;
                            border-radius: 7px !important;
                            font-size: 12px !important;
                          }
                          .job-overlay-company {
                            font-size: 13px !important;
                          }
                          .job-overlay-content {
                            padding: 20px 16px !important;
                          }
                          .job-overlay-title {
                            font-size: 18px !important;
                            margin-bottom: 12px !important;
                          }
                          .job-overlay-actions {
                            flex-direction: column !important;
                          }
                          .job-overlay-actions a {
                            width: 100% !important;
                          }
                          .job-overlay-actions button {
                            width: 100% !important;
                            height: 42px !important;
                          }
                          .job-overlay-actions button svg {
                            width: 16px !important;
                            height: 16px !important;
                          }
                          .job-overlay-role-details {
                            margin-top: 32px !important;
                            padding: 24px 0 !important;
                          }
                          .job-overlay-role-grid {
                            grid-template-columns: 1fr !important;
                          }
                        }
                    `}</style>
                </>
            )}
        </AnimatePresence>
    );
};
export default CompanyPeoplePanel;
